import type { Translations } from "./types";

export const en: Translations = {
  // ── Navigation ──────────────────────────────────────────────────────────────
  "nav.home": "Home",
  "nav.about": "About",
  "nav.services": "Services",
  "nav.talents": "Talents",
  "nav.clients": "Clients",
  "nav.content": "Content",
  "nav.work": "Work",
  "nav.spotlight": "Spotlights",
  "nav.contact": "Contact",
  "nav.login": "Login",
  "nav.dashboard": "Dashboard",
  "nav.signOut": "Sign Out",
  "nav.notifications": "Notifications",
  "nav.profile": "My Profile",
  "nav.castingCart": "Casting Brief Cart",
  "nav.clientDashboard": "Client Dashboard",
  "nav.talentDashboard": "Talent Dashboard",
  "footer.description": "A creative agency specialized in modeling, casting, and video production of creative content.",
  "footer.quickLinks.title": "Quick Links",
  "footer.links.about": "About ARTERIA",
  "footer.links.services": "Services",
  "footer.links.talents": "Talents",
  "footer.links.work": "Work",
  "footer.links.spotlight": "Spotlight",
  "footer.contact.title": "Contact Us",
  "footer.social.title": "Follow Us",
  "footer.social.comingSoon": "{{label}} — coming soon",
  "footer.copyright": "© {{year}} ARTERIA. All rights reserved.",
  "footer.backToTop": "Back to top",

  // ── Common ───────────────────────────────────────────────────────────────────
  "common.signingOut": "Signing out…",

  // ── Notifications ────────────────────────────────────────────────────────────
  "notifications.title": "Notifications",
  "notifications.empty": "You're all caught up!",
  "notifications.noNotificationsYet": "No notifications yet",
  "notifications.unreadCount": "{{count}} unread",
  "notifications.newCount": "{{count}} new",
  "notifications.markAllRead": "Mark all read",
  "notifications.viewAll": "View all notifications →",
  "notifications.newToast": "New Notification",

  // ── Auth ─────────────────────────────────────────────────────────────────────
  "auth.login": "Login",
  "auth.signup": "Sign Up",
  "auth.noAccount": "Don't have an account?",
  "auth.register": "Register here",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.firstName": "First Name",
  "auth.lastName": "Last Name",
  "auth.welcomeBack": "Welcome Back",
  "auth.signIn": "Sign in to your ARTERIA account",
  "auth.worldTagline": "The world of Creativity, Stories, and Authentic expression",
  "auth.toArteriaWorld": "To ARTERIA World",
  "auth.backToHome": "Back to Home",
  "auth.login.passwordPlaceholder": "Enter your password",

  // ── Auth: shared register fields ──────────────────────────────────────────
  "auth.alreadyHaveAccount": "Already have an account?",
  "auth.signInLink": "Sign in",
  "auth.back": "Back",
  "auth.joinArteria": "Join ARTERIA",
  "auth.emailAddress": "Email Address",
  "auth.emailPlaceholder": "your.email@example.com",
  "auth.emailAlreadyRegistered": "This email is already registered.",
  "auth.checkingEmail": "Checking email…",
  "auth.firstNamePlaceholder": "First name",
  "auth.lastNamePlaceholder": "Last name",
  "auth.confirmPassword": "Confirm Password",
  "auth.passwordPlaceholder": "Create a strong password",
  "auth.confirmPasswordPlaceholder": "Confirm your password",
  "auth.phoneNumber": "Phone Number",
  "auth.whatsappNumber": "WhatsApp Number",

  // ── Auth: Register Role Select ─────────────────────────────────────────────
  "auth.registerRoleSelect.title": "Join ARTERIA",
  "auth.registerRoleSelect.step": "Step 1 of 1",
  "auth.registerRoleSelect.talentTitle": "Register as a Talent",
  "auth.registerRoleSelect.talentDescription": "Showcase your skills and get hired.",
  "auth.registerRoleSelect.clientTitle": "Register as a Client",
  "auth.registerRoleSelect.clientDescription": "Find and hire top creative talent.",

  // ── Auth: Register Client ──────────────────────────────────────────────────
  "auth.registerClient.companyName": "Company Name",
  "auth.registerClient.companyNamePlaceholder": "Your company name",
  "auth.registerClient.companyType": "Company Type",
  "auth.registerClient.companyTypePlaceholder": "e.g. Agency, Brand, Production House",
  "auth.registerClient.yourTitle": "Your Title",
  "auth.registerClient.titlePlaceholder": "e.g. CEO, Marketing Manager",
  "auth.registerClient.creatingAccount": "Creating Account...",
  "auth.registerClient.createAccount": "Create Client Account",

  // ── Auth: Register Talent (steps) ──────────────────────────────────────────
  "auth.registerTalent.stepBasicInfo": "Basic Info",
  "auth.registerTalent.stepTalentDetails": "Talent Details",
  "auth.registerTalent.stepGallery": "Gallery",

  // ── Auth: Basic Info Step ──────────────────────────────────────────────────
  "auth.basicInfoStep.title": "Basic Information",
  "auth.basicInfoStep.subtitle": "Tell us who you are",
  "auth.basicInfoStep.continueButton": "Continue to Profile Details",

  // ── Auth: Specialization Step ──────────────────────────────────────────────
  "auth.specializationStep.title": "Your Profile Details",
  "auth.specializationStep.subtitle": "Select your content creator types",
  "auth.specializationStep.contentCreatorTypes": "Content Creator Types",
  "auth.specializationStep.loadingTypes": "Loading content types...",
  "auth.specializationStep.noTypesAvailable": "No content types available",
  "auth.specializationStep.creating": "Creating…",
  "auth.specializationStep.createAccount": "Create Account",

  // ── Auth: Register Content Creator ────────────────────────────────────────
  "auth.registerContentCreator.stepSpecializations": "Specializations",
  "auth.registerContentCreator.title": "Join as Content Creator",
  "auth.registerContentCreator.firstNamePlaceholder": "Enter your first name",
  "auth.registerContentCreator.lastNamePlaceholder": "Enter your last name",
  "auth.dateOfBirth": "Date of Birth",
  "auth.registerContentCreator.validating": "Validating...",
  "auth.registerContentCreator.continueToSpecializations": "Continue to Specializations",

  // ── Auth: Interest Step ────────────────────────────────────────────────────
  "auth.interestStep.title": "What are you interested in?",
  "auth.interestStep.subtitle": "Select one option to continue",
  "auth.interestStep.selectAtLeastOne": "Please select at least one interest to continue",
  "auth.interestStep.continueButton": "Continue to Registration",
  "auth.interestStep.modelingDescription": "Fashion, commercial, editorial, runway",
  "auth.interestStep.contentCreationDescription": "UGC, social media, digital content",
  "auth.interestStep.bothDescription": "I'm interested in both modeling and content creation",

  // ── Auth: Password Field ───────────────────────────────────────────────────
  "auth.passwordField.showPassword": "Show password",
  "auth.passwordField.hidePassword": "Hide password",

  // ── Auth: Questions Section ────────────────────────────────────────────────
  "auth.questionsSection.loading": "Loading questions...",
  "auth.questionsSection.title": "Additional Questions",
  "auth.questionsSection.allRequired": "(all required)",
  "auth.questionsSection.answerAllToContinue": "Please answer all questions to continue",

  // ── Auth: Talent Details Step ──────────────────────────────────────────────
  "auth.talentDetailsStep.title": "Complete Your Talent Profile",
  "auth.talentDetailsStep.subtitle": "Tell us more about yourself to get better opportunities",
  "auth.talentDetailsStep.fixHighlighted": "Please fix the highlighted fields before continuing:",
  "auth.talentDetailsStep.answerAllQuestions": "Please answer all required questions",
  "auth.talentDetailsStep.dateOfBirth": "Date of Birth *",
  "auth.talentDetailsStep.city": "City",
  "auth.talentDetailsStep.cityPlaceholder": "Your city",
  "auth.talentDetailsStep.age": "Age",
  "auth.talentDetailsStep.ageYears": "{{age}} years",
  "auth.talentDetailsStep.ageCalculatedAutomatically": "Calculated automatically",
  "auth.talentDetailsStep.ageCategory": "Age category:",
  "auth.talentDetailsStep.specialties": "Specialties *",
  "auth.talentDetailsStep.specialization": "Specialization",
  "auth.talentDetailsStep.talentCategories": "Talent Categories",
  "auth.talentDetailsStep.gender": "Gender *",
  "auth.talentDetailsStep.skinTone": "Skin Tone",
  "auth.talentDetailsStep.hairType": "Hair Type",
  "auth.talentDetailsStep.eyeColor": "Eye Color",
  "auth.talentDetailsStep.hairColor": "Hair Color",
  "auth.talentDetailsStep.bodyType": "Body Type",
  "auth.talentDetailsStep.lookAlike": "Look Alike",
  "auth.talentDetailsStep.experienceLevel": "Experience Level *",
  "auth.talentDetailsStep.bio": "Previous work / Bio",
  "auth.talentDetailsStep.bioPlaceholder": "Tell us about yourself...",
  "auth.talentDetailsStep.socialMediaProfiles": "Social Media Profiles",
  "auth.talentDetailsStep.physicalDetails": "Physical Details",
  "auth.talentDetailsStep.heightCm": "Height (cm)",
  "auth.talentDetailsStep.weightKg": "Weight (kg)",
  "auth.talentDetailsStep.tshirt": "T-shirt",
  "auth.talentDetailsStep.pant": "Pant",
  "auth.talentDetailsStep.shoe": "Shoe",
  "auth.talentDetailsStep.ratesPricing": "Rates & Pricing",
  "auth.talentDetailsStep.ratesOptional": "Rates (Optional)",
  "auth.talentDetailsStep.ratesPlaceholder": "Egyptian pounds per 12-hours",
  "auth.talentDetailsStep.continueToGallery": "Continue to Gallery",

  // ── Auth: Gallery Upload Step ──────────────────────────────────────────────
  "auth.galleryUploadStep.title": "Profile & Gallery",
  "auth.galleryUploadStep.subtitlePrefix": "Upload a profile photo",
  "auth.galleryUploadStep.required": "(required)",
  "auth.galleryUploadStep.subtitleSuffix": "and at least 6 gallery photos",
  "auth.galleryUploadStep.profilePhoto": "Profile Photo",
  "auth.galleryUploadStep.changePhoto": "Change Photo",
  "auth.galleryUploadStep.uploadPhoto": "Upload Photo",
  "auth.galleryUploadStep.profilePhotoRequired": "Profile photo is required",
  "auth.galleryUploadStep.galleryPhotos": "Gallery Photos",
  "auth.galleryUploadStep.dragDropHere": "Drag & drop photos here, or",
  "auth.galleryUploadStep.browseFiles": "browse files",
  "auth.galleryUploadStep.formatsHint": "JPG, PNG, WEBP · max {{maxMb}}MB each",
  "auth.galleryUploadStep.slotsLeft": "{{count}} slots left",
  "auth.galleryUploadStep.slotsLeftOne": "{{count}} slot left",
  "auth.galleryUploadStep.morePhotosNeeded": "{{count}} more photos needed to continue",
  "auth.galleryUploadStep.morePhotosNeededOne": "{{count}} more photo needed to continue",
  "auth.galleryUploadStep.submitting": "Submitting...",
  "auth.galleryUploadStep.completeRegistration": "Complete Registration",
  "auth.galleryUploadStep.cropProfilePhoto": "Crop Profile Photo",

  // ── Auth: Validation Messages (schema/validator KEYS, translated at the
  //    point each hook stores the message — see auth.schema.ts / register-
  //    talent.utils.ts comments) ────────────────────────────────────────────
  "auth.validation.emailRequired": "Email is required",
  "auth.validation.emailInvalid": "Invalid email address",
  "auth.validation.passwordRequired": "Password is required",
  "auth.validation.loginPasswordMin": "Password must be at least 6 characters",
  "auth.validation.firstNameRequired": "First name is required",
  "auth.validation.firstNameMin": "First name must be at least 2 characters",
  "auth.validation.firstNameMax": "First name must not exceed 50 characters",
  "auth.validation.firstNamePattern": "First name can only contain letters, spaces, hyphens, and apostrophes",
  "auth.validation.lastNameRequired": "Last name is required",
  "auth.validation.lastNameMin": "Last name must be at least 2 characters",
  "auth.validation.lastNameMax": "Last name must not exceed 50 characters",
  "auth.validation.lastNamePattern": "Last name can only contain letters, spaces, hyphens, and apostrophes",
  "auth.validation.phoneRequired": "Phone number is required",
  "auth.validation.phoneInvalid": "Enter a valid phone number for the selected country",
  "auth.validation.countryRequired": "Country is required",
  "auth.validation.whatsappInvalid": "Enter a valid WhatsApp number",
  "auth.validation.passwordMin8": "Password must be at least 8 characters",
  "auth.validation.passwordComplexity": "Password must contain uppercase, lowercase, number, and special character",
  "auth.validation.confirmPasswordRequired": "Please confirm your password",
  "auth.validation.passwordsMismatch": "Passwords do not match",
  "auth.validation.currentPasswordRequired": "Current password is required",
  "auth.validation.newPasswordRequired": "New password is required",
  "auth.validation.confirmNewPasswordRequired": "Please confirm your new password",
  "auth.validation.newPasswordSameAsCurrent": "New password must be different from your current password",
  "auth.validation.min2Chars": "At least 2 characters",
  "auth.validation.max50Chars": "Max 50 characters",
  "auth.validation.lettersOnly": "Letters only",
  "auth.validation.dobRequired": "Date of birth is required",
  "auth.validation.invalidDate": "Invalid date",
  "auth.validation.dobFuture": "Date cannot be in the future",
  "auth.validation.dobInvalid": "Please enter a valid date of birth",
  "auth.validation.nationalityRequired": "Nationality is required",
  "auth.validation.max1000Chars": "Max 1000 characters",
  "auth.validation.experienceRequired": "Experience level is required",
  "auth.validation.genderRequired": "Please select your gender",
  "auth.validation.specialtyRequired": "Select at least one specialty",
  "auth.validation.languageRequired": "Select at least one language",
  "auth.validation.customLanguageRequired": "Please specify your other language",
  "auth.validation.heightPositive": "Height must be a positive number",
  "auth.validation.weightPositive": "Weight must be a positive number",
  "auth.validation.genderMale": "Male",
  "auth.validation.genderFemale": "Female",
  "auth.validation.ageCategoryInfants": "Infants (0-2)",
  "auth.validation.ageCategoryToddler": "Toddler (3-5)",
  "auth.validation.ageCategoryKids": "Kids (6-11)",
  "auth.validation.ageCategoryTeens": "Teens (12-16)",
  "auth.validation.ageCategoryAdults": "Adults (17-25)",
  "auth.validation.ageCategoryPrime": "Prime (26-35)",
  "auth.validation.ageCategoryMature": "Mature (36-50)",
  "auth.validation.ageCategoryClassic": "Classic (+50)",
  "auth.validation.loginFailed": "Failed to sign in. Please check your credentials.",
  "auth.validation.accountCreatedRedirect": "Account created successfully! Please sign in.",
  "auth.validation.createAccountFailed": "Failed to create account.",
  "auth.validation.answerAllQuestions": "Please answer all questions before continuing.",
  "auth.validation.uploadProfilePhotoRequired": "Please upload a profile photo before submitting.",
  "auth.validation.registrationFailed": "Failed to complete registration.",
  "auth.validation.selectContentCreatorType": "Select at least one content creator type.",
  "auth.validation.uploadProfilePhoto": "Please upload a profile photo.",
  "auth.validation.accountCreatedTitle": "Account created!",
  "auth.validation.registrationFailedGeneric": "Registration failed.",
  "auth.galleryUploadStep.cropGalleryPhoto": "Crop Gallery Photo",
  "auth.galleryUploadStep.cropGalleryPhotoWithMore": "Crop Gallery Photo ({{count}} more after)",
  "auth.galleryUploadStep.unsupportedFormat": "{{name}}: unsupported format",
  "auth.galleryUploadStep.exceedsSize": "{{name}}: exceeds {{maxMb}}MB",

  // ── Hero ─────────────────────────────────────────────────────────────────────
  "hero.slogan": "The Native Home of Arts",
  "hero.tagline": "Where creativity lives, stories breathe, and talent unfolds in its purest essence",
  "hero.taglineEmphasized": "Unleash your potential with ARTERIA",
  "hero.becomeTalent": "Become Talent",
  "hero.sendBrief": "Send a Casting Brief",
  "hero.scrollToExplore": "Scroll to explore",
  "talents.view_all": "View All Talents",
  "talents.showcase.badge": "Our Stars",
  "talents.showcase.titlePrefix": "Meet Our",
  "talents.showcase.subtitle": "Discover exceptional talent from our diverse roster of professional models, actors, and creators",
  "talents.showcase.empty": "No talents found",

  // ── About ─────────────────────────────────────────────────────────────────────
  "about.title": "About ARTERIA",
  "about.description": "A revolutionary approach to casting, talent management, and creative content production",
  "about.mainTitle": "Where Stories Come Alive",
  "about.mainDescription": "ARTERIA is the new revolution in casting, talent management, and content creation. A bold shift from tradition to true expression.",
  "about.secondaryDescription": "We cast faces, discover voices, and craft content that tells real stories, drawn from the streets, the culture, and the art that shapes us.",
  "about.approach": "Through creative direction and powerful visuals, we connect raw talent with meaningful narratives that leave a lasting impact.",
  "about.tagline": "ARTERIA is where creativity lives, stories breathe, and talent finds its truest form.",
  "about.stats.talents": "+1500 Talents",
  "about.stats.projects": "+600 Projects",
  "about.stats.satisfaction": "100% Client Satisfaction",
  "about.stats.talentsNetwork": "Talents in our network",
  "about.stats.successfulProjects": "Successful projects delivered",
  "about.card.defaultSubtitle": "Where creativity lives, stories breathe, and talent finds its truest form",
  "about.imageAlt": "About ARTERIA",
  "about.learnMore": "Learn More",
  "about.team.title": "Meet Our Team",
  "about.team.description": "The passionate individuals behind ARTERIA's vision",
  "about.team.member1.name": "Muhammad Ashraf",
  "about.team.member1.role": "Founder & Creative Director",
  "about.team.member1.bio": "Visionary entrepreneur with a passion for arts and technology. Muhammad brings over a decade of experience in creative direction and talent management, revolutionizing the way we discover and nurture artistic talent.",
  "about.team.member2.name": "Mohamed Shaker",
  "about.team.member2.role": "Technical Director",
  "about.team.member2.bio": "Tech innovator and digital architecture expert. Mohamed leads our technical initiatives, bringing cutting-edge solutions to talent management and ensuring seamless digital experiences for our community.",
  "about.team.member3.name": "Ayman Nour",
  "about.team.member3.role": "Creative Strategy Lead",
  "about.team.member3.bio": "Strategic creative thinker with an eye for innovation. Ayman shapes our creative direction, bringing fresh perspectives to talent development and content creation in the digital age.",
  "about.story.title": "Our Story",
  "about.story.content": "Born in 2022, ARTERIA emerged from a vision to spotlight hidden rising talents concealed in the heart of Egyptian streets and the spirit of art.",
  "about.story.description": "When educational institutions ceased nurturing the arts, we stepped in, giving young creatives a chance to learn, grow, and shine.",
  "about.story.approach": "We rejected the wave of imitated content. Instead, we chose real stories rooted in culture, street spirit, and artistic pulse.",
  "about.story.evolution": "ARTERIA became more than just a casting agency it transformed into a creative home where talents meet, learn, and teach us through stories we believe in.",
  "about.vision": "Vision",
  "about.vision.content": "To become the beating heart of authentic talent and storytelling in Egypt, where art, culture, and creativity rise together.",
  "about.mission": "Mission",
  "about.mission.content": "Discovering, nurturing, and showcasing talent through casting and content that speaks the language of the street, soul, and art.",
  "about.values.title": "Core Values",
  "about.values.subtitle": "The principles that guide everything we do at ARTERIA",
  "about.values.ethics": "Ethics",
  "about.values.ethics.description": "We create with honesty, respect, and responsibility, always staying true to our principles.",
  "about.values.creativity": "Creativity",
  "about.values.creativity.description": "We transform bold ideas into powerful visual scenes that move emotions and inspire.",
  "about.values.culture": "Culture",
  "about.values.culture.description": "We celebrate our roots, reflecting our street stories, heritage, and artistic spirit.",
  "about.values.collaboration": "Collaboration",
  "about.values.collaboration.description": "We rise together through learning, sharing, and mutual support.",
  "about.values.integrity": "Integrity",
  "about.values.integrity.description": "We stand by what we believe in and represent only what is true and authentic.",
  "about.values.authenticity": "Authenticity",
  "about.values.authenticity.description": "We embrace honest expression and celebrate the unique distinction in every talent and project.",

  // ── Services ──────────────────────────────────────────────────────────────────
  "services.title": "Our Services",
  "services.subtitle": "Turning Your Ideas into Reality",
  "services.mainDescription": "Comprehensive solutions for all your talent and content needs",
  "services.casting.section.title": "Casting Agency",
  "services.casting.section.description": "Our casting agency specializes in identifying and securing diverse talent for all projects, commercials, films, and digital content.",
  "services.casting.section.cta": "Send Casting Brief",
  "services.casting.section.features": [
    "Diverse talent database with 1500+ verified models",
    "Perfect project matching using advanced algorithms",
    "Professional screening and background checks",
    "Quick turnaround within 24-48 hours",
    "Custom talent recommendations",
    "Flexible booking and scheduling",
  ],
  "services.content.section.title": "Content Production",
  "services.content.section.description": "We create impactful, high-quality visuals for every platform and purpose.",
  "services.content.section.cta": "Get Content Quote",
  "services.content.section.features": [
    "Fiction & non-fiction film production",
    "Brand campaigns and commercial content",
    "Social media content creation",
    "Documentary filmmaking",
    "Full production service from concept to delivery",
    "Post-production and editing services",
  ],
  "services.process.section.title": "Our Process",
  "services.process.section.subtitle": "From concept to delivery, we ensure every project exceeds expectations",
  "services.process.section.steps": [
    { step: "01", title: "Discovery & Planning", description: "We understand your vision, goals, and requirements to create the perfect strategy." },
    { step: "02", title: "Talent Selection & Production", description: "Our team carefully selects the right talent and begins the creative production process." },
    { step: "03", title: "Delivery & Support", description: "We deliver exceptional results and provide ongoing support for your success." },
  ],
  "services.main.cta.title": "Ready to Start Your Project?",
  "services.main.cta.subtitle": "Let's bring your vision to life with our expert talent and production services",
  "services.main.cta.casting": "Send Casting Brief",
  "services.main.cta.content": "Get Content Quote",
  "services.content.title": "Content Production",
  "services.content.description": "We create high-quality, impactful visuals for every platform and purpose.",
  "services.content.features.films": "Fiction & documentary film production",
  "services.content.features.campaigns": "Brand campaigns and commercial content",
  "services.content.features.social": "Social media content creation",
  "services.content.features.documentary": "Documentary filmmaking",
  "services.content.features.production": "Full production service from concept to delivery",
  "services.content.features.postproduction": "Post-production and editing services",
  "services.content.cta": "Request Quote",
  "services.process.title": "Our Process",
  "services.process.subtitle": "From concept to delivery, we ensure every project exceeds expectations",
  "services.process.step1.title": "Discovery & Planning",
  "services.process.step1.description": "We understand your vision, goals, and requirements to formulate the perfect strategy.",
  "services.process.step2.title": "Talent Selection & Production",
  "services.process.step2.description": "Our team carefully selects the right talent and begins the creative production process.",
  "services.process.step3.title": "Delivery & Support",
  "services.process.step3.description": "We deliver exceptional results and provide ongoing support to ensure your success.",
  "services.cta.title": "Ready to Start Your Project?",
  "services.cta.subtitle": "Let us bring your vision to life with our specialized talent and production services",
  "services.cta.casting": "Send Casting Request",
  "services.cta.content": "Request Content Quote",

  // ── Talents ───────────────────────────────────────────────────────────────────
  "talents.title": "Looking for Talent?",
  "talents.subtitle": "It's easier than ever.",
  "talents.description": "For directors, producers, photographers, and creatives: We've streamlined the entire casting process.",
  "talents.library": "Explore our talent library of verified models and creatives. Search by age, look, skill, or type all in one place.",
  "talents.library.title": "Explore All Our Talents",
  "talents.library.description": "Discover our stars through a diverse library with everything you need for your project.",
  "talents.features.nodelay": "Fast Turnaround",
  "talents.features.instant": "Instant Access to Verified Talents",
  "talents.features.nobackforth": "Streamlined Process",
  "talents.features.streamlined": "Streamlined selection process",
  "talents.features.righttalent": "Just the right talent",
  "talents.features.perfectmatch": "Perfect match every time",
  "talents.projects": "Whether you're working on a film, series, commercial, UGC, or digital content ARTERIA is ready for you.",
  "talents.steps.title": "How It Works",
  "talents.steps.1.title": "Submit Brief",
  "talents.steps.1.description": "Tell us about your project requirements and vision",
  "talents.steps.2.title": "Get Recommendations",
  "talents.steps.2.description": "Receive curated talent selections within 24 hours",
  "talents.steps.3.title": "Start Creating",
  "talents.steps.3.description": "Begin your project with perfect talent matches",
  "talents.cta.brief": "Send a Casting Brief",
  "talents.cta.subtitle": "Free consultation • 24-hour response • Custom quotations",
  "talents.mainCta": "Discover Talents. Save Time. Create Better.",
  "talents.filters": "Filters",
  "talents.hero.badge": "Talent Hub",
  "talents.hero.description": "Discover the faces that bring stories to life. Each talent in our network is carefully selected for their unique presence and professional excellence.",
  "talents.clearAll": "Clear all",
  "talents.foundOne": "talent found",
  "talents.foundMany": "talents found",
  "talents.noMatch.title": "No talents match your search",
  "talents.noMatch.subtitle": "Try adjusting your filters or search term",
  "talents.clearFilters": "Clear Filters",
  "talents.loadingMore": "Loading more talents…",
  "talents.filters.talentType": "Talent Type",
  "talents.filters.gender": "Gender",
  "talents.filters.age": "Age",
  "talents.filters.bodyType": "Body Type",
  "talents.filters.experience": "Experience",
  "talents.filters.skinTone": "Skin Tone",
  "talents.filters.hairType": "Hair Type",
  "talents.filters.hairColor": "Hair Color",
  "talents.filters.eyeColor": "Eye Color",
  "talents.filters.weight": "Weight",
  "talents.filters.height": "Height",
  "talents.filters.languages": "Languages",
  "talents.filters.lookAlike": "Look Alike",
  "talents.filters.category": "Category",
  "talents.filters.clearAllFilters": "Clear All Filters",
  "talents.filters.searchPlaceholder": "Search talents...",
  "talents.filters.experience.beginner": "Beginner (0-1 years)",
  "talents.filters.experience.intermediate": "Intermediate (2-5 years)",
  "talents.filters.experience.professional": "Professional (5+ years)",
  "talents.filters.experience.expert": "Expert (10+ years)",

  // ── Content ───────────────────────────────────────────────────────────────────
  "content.title": "Turning ideas into",
  "content.pageTitle": "Content Creation",
  "content.titleHighlight": "breathing stories",
  "content.description": "We create bold, story-driven content. From documentaries and digital shows to brand films, fashion videos, and reels — every frame we create has soul, meaning, and longevity.",
  "content.discover": "Discover More",
  "content.project": "Project",
  "content.projectDescription": "Brand storytelling that connects and inspires",
  "content.types.documentaries.title": "Documentary Films",
  "content.types.documentaries.desc": "Authentic storytelling that captures real moments and genuine emotions with cinematic quality.",
  "content.types.fashion.title": "Fashion Films",
  "content.types.fashion.desc": "Stylish visual narratives that showcase fashion and beauty with cinematic flair.",
  "content.types.social.title": "Social Media Content",
  "content.types.social.desc": "Engaging posts, stories, and reels that capture attention and drive engagement across all platforms.",
  "content.types.brand.title": "Brand Content",
  "content.types.brand.desc": "Compelling brand stories that connect with audiences and build lasting impressions.",
  "content.contentTypes": [
    { title: "Social Media Content", desc: "Engaging posts, stories, and reels that capture attention and drive engagement across all platforms." },
    { title: "Documentary Filmmaking", desc: "Authentic storytelling that captures real moments and genuine emotions with cinematic quality." },
    { title: "Commercial Ads", desc: "Compelling advertisements that connect with audiences and drive brand awareness and sales." },
    { title: "UGC Content", desc: "User-generated style content that feels authentic and relatable to modern audiences." },
    { title: "Event & Corporate Films", desc: "Professional documentation of corporate events, conferences, and brand activations." },
    { title: "Sketches & Entertainment", desc: "Creative sketches and entertainment content that brings joy and builds community." },
    { title: "Brand Photography", desc: "Stunning visual assets that represent your brand with style and authenticity." },
  ],
  "content.process": [
    { step: "01", title: "Vision Discovery", description: "We dive deep into your story, understanding your vision, audience, and goals to create the perfect narrative strategy." },
    { step: "02", title: "Creative Development", description: "Our team develops concepts, scripts, and visual treatments that bring your story to life with authenticity and impact." },
    { step: "03", title: "Production Excellence", description: "From casting to filming, we manage every detail with precision, ensuring the highest quality output." },
    { step: "04", title: "Post-Production Magic", description: "Our editors and designers craft the final piece, adding the finishing touches that make your content unforgettable." },
  ],
  "content.hero.titleLine1": "We tell stories that the world",
  "content.hero.titleLine2": "needs to remember",
  "content.hero.description": "Every frame, every sound, every word, crafted to move hearts, spark conversations, and leave an imprint. From a 15-second reel to a feature-length documentary, we turn ideas into stories worth sharing.",
  "content.hero.cta": "Tell It With Us",
  "content.whatWeCreate.badge": "What We Create",
  "content.whatWeCreate.titlePrefix": "Content That",
  "content.whatWeCreate.titleHighlight": "Connects",
  "content.whatWeCreate.subtitle": "From social media content to feature documentaries, we create visual stories that resonate with audiences and drive meaningful engagement.",
  "content.featured.titlePrefix": "Featured",
  "content.featured.titleHighlight": "Content",
  "content.featured.subtitle": "A showcase of our most impactful content across different formats",
  "content.noVideo": "No Video",
  "content.metrics": "Likes: {{likes}} · Views: {{views}}",
  "content.process.titlePrefix": "Our Creative",
  "content.process.titleHighlight": "Process",
  "content.process.subtitle": "From initial concept to final delivery, we ensure every project exceeds expectations",
  "content.portfolioShowcase": [
    { title: "Social Media Revolution", description: "A viral campaign that redefined how brands connect with Egyptian youth", category: "Social Media", metrics: "2M+ Views, 150K Engagements" },
    { title: "Streets of Cairo", description: "Documentary series showcasing the untold stories of Egyptian street artists", category: "Documentary", metrics: "Award-Winning Series" },
    { title: "Brand Transformation", description: "Complete visual rebrand through authentic storytelling and local talent", category: "Commercial", metrics: "300% Brand Awareness Increase" },
    { title: "Cultural Heritage", description: "Preserving Egyptian traditions through modern visual storytelling", category: "Brand Film", metrics: "International Recognition" },
    { title: "Youth Voices", description: "Amplifying the voices of young Egyptian creators and their stories", category: "UGC", metrics: "500K+ Organic Reach" },
    { title: "Corporate Excellence", description: "Professional event coverage that captures the essence of business success", category: "Corporate", metrics: "Fortune 500 Clients" },
  ],

  // ── Spotlight ─────────────────────────────────────────────────────────────────
  "spotlight.title": "Spotlight",
  "spotlight.subtitle": "Stories & Articles",
  "spotlight.description": "Each face paints a scene, and every voice builds a world of creativity. Welcome to ARTERIA Spotlight",
  "spotlight.readMore": "Read More",
  "spotlight.page.blogs": "Blogs",
  "spotlight.searchPlaceholder": "Search blogs...",
  "spotlight.empty": "No blogs found.",
  "spotlight.viewAll": "View All Our Blogs",
  "spotlight.categories.all": "All",
  "spotlight.categories.art": "Art",
  "spotlight.categories.news": "News",
  "spotlight.readTime": "min read",
  "spotlight.author": "Author",
  "spotlight.date": "Date",
  "spotlight.category": "Category",
  "spotlight.categories.industry": "Industry Insights",
  "spotlight.categories.casting": "Casting",
  "spotlight.categories.company": "Company",
  "spotlight.categories.creative": "Creative",
  "spotlight.blogs.1.title": "The Future of Authentic Storytelling in Egyptian Cinema",
  "spotlight.blogs.1.excerpt": "How ARTERIA is revolutionizing the way we cast talent and create content that truly represents Egyptian culture and streets.",
  "spotlight.blogs.1.date": "Dec 15, 2024",
  "spotlight.blogs.1.author": "ARTERIA Team",
  "spotlight.blogs.2.title": "Behind the Scenes: Casting for Authenticity",
  "spotlight.blogs.2.excerpt": "Our approach to finding real faces that tell genuine stories.",
  "spotlight.blogs.2.date": "Dec 10, 2024",
  "spotlight.blogs.2.author": "Sarah Ahmed",
  "spotlight.blogs.3.title": "From Streets to Screen: Our Journey",
  "spotlight.blogs.3.excerpt": "The story of how ARTERIA became the home for raw, undiscovered talent.",
  "spotlight.blogs.3.date": "Dec 8, 2024",
  "spotlight.blogs.3.author": "Mohamed Hassan",
  "spotlight.blogs.4.title": "Creative Direction in the Digital Age",
  "spotlight.blogs.4.excerpt": "Blending traditional storytelling with modern digital platforms.",
  "spotlight.blogs.4.date": "Dec 5, 2024",
  "spotlight.blogs.4.author": "Layla Mansour",

  // ── Work ─────────────────────────────────────────────────────────────────────
  "work.title": "Our Work & Client Stories",
  "work.description": "Discover the stories behind our success: real projects, real impact, real voices.",
  "work.cta": "View Our Portfolio",
  "work.showcase.titlePrefix": "Our",
  "work.showcase.titleHighlight": "Work",
  "work.showcase.titleSuffix": "& Testimonials",
  "work.loadError.title": "Unable to load works",
  "work.loadError.subtitle": "Check your internet connection and try again.",
  "work.empty": "No works yet.",
  "work.brands.title": "Trusted by Leading Brands",
  "work.brands.description": "We partner with the biggest brands in the market to create outstanding content production experiences.",
  "work.brands.subtitle": "We've collaborated with industry leaders to create authentic, impactful content",
  "work.testimonials.title": "Client Testimonials",
  "work.testimonials.description": "Hear from our clients about their experiences working with ARTERIA and how we brought their visions to life.",
  "work.hero.badge": "Our Portfolio",
  "work.featured.titlePrefix": "Featured",
  "work.featured.titleHighlight": "Projects",
  "work.featured.subtitle": "A showcase of our most impactful work across various industries and creative disciplines",
  "work.ariaPlay": "Play {{title}}",
  "work.ariaPlaying": "Playing {{title}}",
  "work.closeVideo": "Close video",
  "work.noVideoAvailable": "No video available",
  "work.testimonials.empty": "No testimonials yet.",
  "work.testimonials.projectLabel": "Project:",
  "work.brands.empty": "No brands to display.",
  "albums.viewImageAlt": "View image {{index}}",
  "albums.galleryImageAlt": "Gallery image {{index}}",
  "albums.previousImage": "Previous image",
  "albums.nextImage": "Next image",
  // ── Talent Profile ─────────────────────────────────────────────────────────────
  "talentProfile.backToTalents": "Back to Talents",
  "talentProfile.notFound": "Talent not found",
  "talentProfile.label": "Talent Profile",
  "talentProfile.defaultName": "Talent",
  "talentProfile.noPhoto": "No Photo",
  "talentProfile.addedToBrief": "Added to Brief",
  "talentProfile.castNow": "Cast {{name}}",
  "talentProfile.thisTalent": "This Talent",
  "talentProfile.yrs": "yrs",
  "talentProfile.cm": "cm",
  "talentProfile.kg": "kg",
  "talentProfile.yrsExp": "yrs exp",
  "talentProfile.physical": "Physical",
  "talentProfile.skills": "Skills",
  "talentProfile.languages": "Languages",
  "talentProfile.portfolio": "Portfolio",
  "talentProfile.attrs.gender": "Gender",
  "talentProfile.attrs.eyeColor": "Eye Color",
  "talentProfile.attrs.hairColor": "Hair Color",
  "talentProfile.attrs.hairType": "Hair Type",
  "talentProfile.attrs.skinTone": "Skin Tone",
  "talentProfile.attrs.bodyType": "Body Type",
  "talentProfile.attrs.nationality": "Nationality",
  "talentProfile.attrs.city": "City",
  "talentProfile.attrs.shoeSize": "Shoe Size",
  "talentProfile.attrs.pantSize": "Pant Size",
  "talentCard.showProfile": "Show Profile",
  "talentCard.like": "Like",
  "talentCard.unlike": "Unlike",
  "talentCard.added": "Added",
  "talentCard.hireNow": "Hire Now",
  "talentCard.castNow": "Cast Now",
  "talentCard.eyes": "Eyes",
  "talentCard.hair": "Hair",
  "talentCard.skin": "Skin",
  "talentCard.country": "Country",
  "talentCard.lang": "Lang",
  "gallery.title": "Portfolio Gallery",
  "gallery.uploadPhotos": "Upload Photos",
  "gallery.uploading": "Uploading…",
  "gallery.all": "All",
  "gallery.photos": "Photos",
  "gallery.videos": "Videos",
  "gallery.empty": "No portfolio media available yet",
  "gallery.emptySubtitle": "Check back soon for updates!",
  "gallery.imageAlt": "Portfolio",
  "blogs.title": "Blog Posts",
  "comments.title": "Client Feedback",
  // ── Shared: Status labels ──────────────────────────────────────────────────
  "status.active": "Active",
  "status.pending": "Pending",
  "status.inReview": "In review",
  "status.approved": "Approved",
  "status.rejected": "Rejected",
  "status.completed": "Completed",
  "status.draft": "Draft",
  "priority.urgent": "Urgent",
  "priority.high": "High",
  "priority.medium": "Medium",
  "priority.low": "Low",
  // ── Client Dashboard ──────────────────────────────────────────────────────────
  "clientDashboard.portalBadge": "Client Portal",
  "clientDashboard.title": "Dashboard",
  "clientDashboard.welcomeBack": "Welcome back, {{name}}",
  "clientDashboard.writeBlog": "Write blog",
  "clientDashboard.newCastingBrief": "New casting brief",
  "clientDashboard.tabOverview": "Overview",
  "clientDashboard.tabMyBlogs": "My Blogs",
  "clientDashboard.tabMyBriefs": "My Briefs",
  "clientDashboard.stats.totalBriefs": "Total briefs",
  "clientDashboard.stats.pendingBriefs": "Pending briefs",
  "clientDashboard.stats.inReview": "In review",
  "clientDashboard.stats.completed": "Completed",
  "clientDashboard.stats.totalBlogs": "Total blogs",
  "clientDashboard.stats.publishedBlogs": "Published blogs",
  "clientDashboard.quickActions": "Quick actions",
  "clientDashboard.createBlogAction": "Create blog",
  "clientDashboard.myBlogsAction": "My blogs",
  "clientDashboard.myBriefsAction": "My briefs",
  "clientDashboard.editProfileAction": "Edit profile",
  "clientDashboard.recentBriefs": "Recent briefs",
  "clientDashboard.viewAll": "View all →",
  "clientDashboard.blogCountOne": "{{count}} blog",
  "clientDashboard.blogCountOther": "{{count}} blogs",
  "clientDashboard.briefCountOne": "{{count}} brief",
  "clientDashboard.briefCountOther": "{{count}} briefs",
  "clientDashboard.newBlog": "New blog",
  "clientDashboard.noBlogsYet": "No blogs yet",
  "clientDashboard.createFirstBlog": "Create your first blog",
  "clientDashboard.newBrief": "New brief",
  "clientDashboard.noBriefsYet": "No casting requests yet",
  "clientDashboard.submitFirstBrief": "Submit your first brief",
  "clientDashboard.viewBriefTooltip": "View brief",
  "clientDashboard.tableProjectType": "Project type",
  "clientDashboard.tableTalents": "Talents",
  "clientDashboard.tableSubmitted": "Submitted",
  "clientDashboard.loadBriefsError": "Failed to load briefs",
  "clientDashboard.loadDataError": "Error loading data",
  "clientDashboard.getBriefFailed": "Failed to load brief",
  "clientDashboard.titleRequired": "Title is required",
  "clientDashboard.contentRequired": "Content is required",
  "clientDashboard.categoryRequired": "Category is required",
  "clientDashboard.blogUpdatedMsg": "Blog updated! Pending admin review.",
  "clientDashboard.blogSubmittedMsg": "Blog submitted! Pending admin review.",
  "clientDashboard.saveBlogFailed": "Failed to save blog.",
  "clientDashboard.deleteBlogTitle": "Delete Blog?",
  "clientBriefDetailsModal.title": "Brief Details",
  "clientBriefDetailsModal.loading": "Loading brief…",
  "clientBriefDetailsModal.submittedOn": "Submitted {{date}}",
  "clients.title": "Clients",
  "clients.hero.badge": "Client Hub",
  "clients.hero.description": "Explore the brands and companies that trust our talent network to bring their creative visions to life.",
  "clients.searchPlaceholder": "Search clients...",
  "clients.foundOne": "client found",
  "clients.foundMany": "clients found",
  "clients.noMatch.title": "No clients match your search",
  "clients.loadMore": "Load More Clients",
  "submitBlog.badge": "Spotlight",
  "submitBlog.title": "Submit a Blog",
  "submitBlog.subtitle": "Your post will be reviewed by our team before publishing",
  "submitBlog.titleFieldLabel": "Title *",
  "submitBlog.titlePlaceholder": "Your blog title",
  "submitBlog.introPlaceholder": "Short summary (optional)",
  "submitBlog.contentFieldLabel": "Content *",
  "submitBlog.titleAndContentRequired": "Title and content are required",
  "submitBlog.submittedSuccess": "Blog submitted for review!",
  "submitBlog.submitFailed": "Failed to submit blog",
  "submitBlog.submitButton": "Submit for Review",
  "castingBriefForm.badge": "Casting Brief",
  "castingBriefForm.heroTitlePrefix": "Send a",
  "castingBriefForm.heroSubtitle1": "Just drop the details it takes less than 5 minutes.",
  "castingBriefForm.heroSubtitle2": "Or type your wildest vision in the notes and we'll handle the rest!",
  "castingBriefForm.formTitle": "Tell Us About Your Project",
  "castingBriefForm.formSubtitle": "Fill out the details and we'll match you with perfect talents",
  "castingBriefForm.draftRestored": "We restored the draft you were filling in before you were signed out.",
  "castingBriefForm.firstNameLabel": "Your First Name *",
  "castingBriefForm.lastNameLabel": "Your Last Name *",
  "castingBriefForm.emailLabel": "Email Address *",
  "castingBriefForm.phoneLabel": "Phone Number *",
  "castingBriefForm.companyLabel": "Company",
  "castingBriefForm.companyNotSet": "Not set —",
  "castingBriefForm.addCompanyLink": "add your company to your profile",
  "castingBriefForm.projectTypeLabel": "Project Type *",
  "castingBriefForm.loadingProjectTypes": "Loading project types…",
  "castingBriefForm.selectProjectType": "Select project type",
  "castingBriefForm.projectTypesError": "Couldn't load project types — you won't be able to submit until this is fixed.",
  "castingBriefForm.projectDescriptionLabel": "Project Description *",
  "castingBriefForm.projectDescriptionPlaceholder": "Describe your project, vision, and what you're looking for...",
  "castingBriefForm.locationLabel": "Location",
  "castingBriefForm.locationPlaceholder": "e.g. Cairo, Egypt",
  "castingBriefForm.shootDateLabel": "Shoot Date",
  "castingBriefForm.budgetLabel": "Budget Range (EGP)",
  "castingBriefForm.specialtiesLabel": "Talent Specialties",
  "castingBriefForm.loadingSpecialties": "Loading specialties…",
  "castingBriefForm.specialtiesError": "Couldn't load specialties.",
  "castingBriefForm.subOptions": "Sub-options",
  "castingBriefForm.demographicsTitle": "Talent Demographics",
  "castingBriefForm.cityLabel": "City",
  "castingBriefForm.cityPlaceholder": "e.g. Cairo",
  "castingBriefForm.physicalAttributesTitle": "Physical Attributes",
  "castingBriefForm.heightRangeLabel": "Height Range (cm)",
  "castingBriefForm.weightRangeLabel": "Weight Range (kg)",
  "castingBriefForm.talentTypeLabel": "Talent Type",
  "castingBriefForm.experienceLevelLabel": "Experience Level",
  "castingBriefForm.anyLevel": "Any level",
  "castingBriefForm.availableFromLabel": "Available From",
  "castingBriefForm.availableToLabel": "Available To",
  "castingBriefForm.recommendedTalentsLabel": "Recommended Talents",
  "castingBriefForm.loadingTalents": "Loading talents…",
  "castingBriefForm.recommendedError": "Couldn't load recommended talents right now — try adjusting a filter or check back shortly.",
  "castingBriefForm.noMatchingTalents": "No matching talents found.",
  "castingBriefForm.addTalentAria": "Add {{name}} to your brief",
  "castingBriefForm.thisTalent": "this talent",
  "castingBriefForm.unnamedTalent": "Unnamed",
  "castingBriefForm.selectedTalentsLabel": "Selected Talents ({{count}})",
  "castingBriefForm.removeTalentAria": "Remove {{name}} from your brief",
  "castingBriefForm.notesLabel": "Additional Notes",
  "castingBriefForm.notesPlaceholder": "Any extra details, vision, special requirements, or creative direction…",
  "castingBriefForm.submitting": "Submitting…",
  "castingBriefForm.submitButton": "Submit Casting Brief",
  "castingBriefForm.submittedSuccess": "Brief submitted! Redirecting…",
  "castingBriefForm.submitFailed": "Failed to submit. Please try again.",
  "castingBriefForm.responseTime": "We'll get back to you within 24 hours",
  "castingBriefForm.responseAvailability": "24/7 Response",
  "castingBriefForm.any": "Any",
  "castingBriefForm.min": "Min",
  "castingBriefForm.max": "Max",
  "contentCreatorTalentCard.years": "years",
  "contentCreatorTalentCard.added": "Added",
  "contentCreatorTalentCard.castNow": "Cast Now",
  "contentCreatorTalentCard.viewProfile": "View Profile",
  "contentCreatorTalents.loadFailed": "Failed to load talents.",
  "contentCreatorTalents.profileLoadFailed": "Failed to load talent",
  "contentCreatorProfile.updatePhotoFailed": "Failed to update photo",
  "contentCreatorProfile.notFound": "Profile not found.",
  "blogModal.tabDetails": "Details",
  "blogModal.tabContent": "Content",
  "blogModal.tabMedia": "Media & Tags",
  "blogModal.editTitle": "Edit blog",
  "blogModal.createTitle": "Create blog",
  "blogModal.titleLabel": "Title",
  "blogModal.titlePlaceholder": "Blog post title",
  "blogModal.introLabel": "Intro",
  "blogModal.introPlaceholder": "Short description shown in listings",
  "blogModal.categoryLabel": "Category",
  "blogModal.selectCategory": "Select category",
  "blogModal.contentPlaceholder": "Write your blog content here…",
  "blogModal.coverImageLabel": "Cover image",
  "blogModal.uploadCoverImage": "Click to upload cover image",
  "blogModal.tagsLabel": "Tags",
  "blogModal.tagPlaceholder": "Add tag, press Enter",
  "blogModal.back": "← Back",
  "blogModal.next": "Next →",
  "blogModal.saving": "Saving…",
  "blogModal.submitting": "Submitting…",
  "blogModal.saveChanges": "Save changes",
  "blogModal.submitBlog": "Submit blog",
  // ── Profile (shared page) ─────────────────────────────────────────────────────
  "profile.roleAdmin": "Admin",
  "profile.roleClient": "Client",
  "profile.roleTalent": "Talent",
  "profile.defaultName": "User",
  "profile.uploadImageTooltip": "Upload image",
  "profile.removeImageTooltip": "Remove image",
  "profile.confirmDeleteImage": "Are you sure you want to remove your profile image?",
  "profile.invalidImageType": "Please upload a valid image (JPEG, PNG, or WebP)",
  "profile.imageTooLarge": "Image size must be less than 5MB",
  "profile.uploadFailed": "Failed to upload image",
  "profile.deleteFailed": "Failed to delete image",
  "profile.loadFailed": "Failed to load profile.",
  "profile.previewTitle": "Preview Profile Image",
  "profile.uploading": "Uploading...",
  "changePassword.title": "Change Password",
  "changePassword.currentPassword": "Current Password",
  "changePassword.newPassword": "New Password",
  "changePassword.confirmNewPassword": "Confirm New Password",
  "changePassword.updating": "Updating...",
  "changePassword.updateButton": "Update Password",
  "changePassword.successToast": "Password changed successfully",
  "changePassword.failToast": "Failed to change password",
  "clientProfileForm.personalInfo": "Personal Info",
  "clientProfileForm.firstName": "First Name",
  "clientProfileForm.lastName": "Last Name",
  "clientProfileForm.email": "Email",
  "clientProfileForm.title": "Title",
  "clientProfileForm.companyInfo": "Company Info",
  "clientProfileForm.companyName": "Company Name",
  "clientProfileForm.saveChanges": "Save Changes",
  "clientProfilePage.titlePlaceholder": "Title (CEO, Manager...)",
  "clientProfilePage.noCompany": "No Company",
  "clientProfilePage.cropTitle": "Crop Profile Photo",
  // ── Admin / Talent Profile Forms ──────────────────────────────────────────────
  "adminProfileForm.accountInfo": "Account Info",
  "talentProfileForm.talentType": "Talent Type",
  "talentProfileForm.previousWork": "Previous Work",
  "talentProfileForm.experienceYears": "Experience (years)",
  "talentProfileForm.ratesPer": "Rates Per",
  "talentProfileForm.ratesPerPlaceholder": "e.g. per day, per hour",
  "talentProfileForm.physicalAttributes": "Physical Attributes",
  // ── Talent Gallery / Questions ─────────────────────────────────────────────────
  "talentGallery.title": "Gallery",
  "talentGallery.loadFailed": "Failed to load gallery.",
  "talentGallery.uploadFailed": "Upload failed. Please try again.",
  "talentGallery.deleteFailed": "Failed to delete image.",
  "talentGallery.confirmDelete": "Delete this image?",
  "talentGallery.addPhotos": "Add Photos",
  "talentGallery.uploadPhotosOne": "Upload {{count}} Photo",
  "talentGallery.uploadPhotosOther": "Upload {{count}} Photos",
  "talentGallery.emptyState": "No gallery images yet. Click to upload.",
  "talentGallery.cropPhotoTitle": "Crop Photo",
  "talentGallery.moreAfterSuffix": "{{count}} more after",
  "talentQuestions.loadFailed": "Failed to load questions",
  "talentQuestions.waitForLoad": "Please wait for questions to load",
  "talentQuestions.answerAll": "Please answer all questions",
  "talentQuestions.title": "Registration Questions",
  // ── Talent Dashboard ───────────────────────────────────────────────────────────
  "talentDashboard.welcomeBack": "Welcome back",
  "talentDashboard.title": "Talent Dashboard",
  "talentDashboard.stats.totalBlogs": "Total Blogs",
  "talentDashboard.stats.approvedBlogs": "Approved Blogs",
  "talentDashboard.stats.pendingBlogs": "Pending Blogs",
  "talentDashboard.stats.castingBriefs": "Casting Briefs",
  "talentDashboard.pendingCount": "{{count}} pending",
  "talentDashboard.quickActionsTitle": "Quick Actions",
  "talentDashboard.quickActionsSubtitle": "Jump into your most important tasks",
  "talentDashboard.actions.createBlogTitle": "Create Blog",
  "talentDashboard.actions.createBlogDesc": "Write and submit a new blog post",
  "talentDashboard.actions.myBlogsTitle": "My Blogs",
  "talentDashboard.actions.myBlogsDesc": "View and manage your blog posts",
  "talentDashboard.actions.castingBriefsDesc": "View briefs you've been selected for",
  "talentDashboard.actions.editProfileTitle": "Edit Profile",
  "talentDashboard.actions.editProfileDesc": "Update your talent profile",
  "talentDashboard.editBlogPostTitle": "Edit Blog Post",
  "talentDashboard.createBlogPostTitle": "Create Blog Post",
  "talentDashboard.blogFormSubtitle": "Your post will be reviewed before publishing",
  "talentDashboard.closeFormAria": "Close form",
  "talentDashboard.titlePlaceholder": "Enter blog title",
  "talentDashboard.contentPlaceholder": "Write your content here…",
  "talentDashboard.removeImageAria": "Remove image",
  "talentDashboard.coverPreviewAlt": "Preview",
  "talentDashboard.tagPlaceholder": "Type a tag and press Enter",
  "talentDashboard.removeTagAria": "Remove tag {{tag}}",
  "talentDashboard.updateBlog": "Update",
  "talentDashboard.submitForReview": "Submit for Review",
  "talentDashboard.briefsSelectedForOne": "{{count}} brief you've been selected for",
  "talentDashboard.briefsSelectedForOther": "{{count}} briefs you've been selected for",
  "talentDashboard.closeBriefsAria": "Close briefs",
  "talentDashboard.noBriefsYet": "No casting briefs yet",
  "talentDashboard.noBriefsYetSubtitle": "You haven't been selected for any briefs yet",
  "talentDashboard.untitledBrief": "Untitled",
  "talentDashboard.selectedBadge": "Selected",
  "talentDashboard.myBlogPostsTitle": "My Blog Posts",
  "talentDashboard.postsCountOne": "{{count}} post",
  "talentDashboard.postsCountOther": "{{count}} posts",
  "talentDashboard.closeBlogListAria": "Close blog list",
  "talentDashboard.noBlogPostsYet": "No blog posts yet",
  "talentDashboard.createFirstBlogPost": "Create your first blog post to get started",
  "talentDashboard.recentActivityTitle": "Recent Activity",
  "talentDashboard.recentActivitySubtitle": "Your latest blog and brief activity",
  "talentDashboard.noActivityYet": "No recent activity yet",
  "talentDashboard.activityBlogApproved": "Blog approved: \"{{title}}\"",
  "talentDashboard.activityBlogRejected": "Blog rejected: \"{{title}}\"",
  "talentDashboard.activityBlogSubmitted": "Blog submitted: \"{{title}}\"",
  "talentDashboard.activityAssignedBrief": "Assigned to casting brief: \"{{desc}}\"",
  // ── Content Brief Form (public) ────────────────────────────────────────────────
  "contentBriefForm.fields.nameLabel": "Name",
  "contentBriefForm.fields.namePlaceholder": "Your full name",
  "contentBriefForm.fields.emailPlaceholder": "your.email@example.com",
  "contentBriefForm.fields.phoneLabel": "Phone Number",
  "contentBriefForm.fields.phonePlaceholder": "+20 XXX XXX XXXX",
  "contentBriefForm.fields.ideaLabel": "Your Idea",
  "contentBriefForm.fields.ideaPlaceholder": "Describe your content idea in detail. What's the story you want to tell? What's the message? Who's your target audience? What style or mood are you aiming for? The more details you share, the better we can understand your vision and bring it to life exactly as you imagine it.",
  "contentBriefForm.fields.equipmentLabel": "Equipment or Production Needed",
  "contentBriefForm.fields.equipmentPlaceholder": "Let us know if you have specific equipment needs, production requirements, locations in mind, timeline constraints, or budget considerations. This helps us plan the perfect production approach for your project.",
  "contentBriefForm.contentTypes.shortForm.title": "Short-Form Content",
  "contentBriefForm.contentTypes.shortForm.item1": "Social media reels & stories",
  "contentBriefForm.contentTypes.shortForm.item2": "TikTok videos & trends",
  "contentBriefForm.contentTypes.shortForm.item3": "Instagram content",
  "contentBriefForm.contentTypes.shortForm.item4": "YouTube Shorts",
  "contentBriefForm.contentTypes.longForm.title": "Long-Form Content",
  "contentBriefForm.contentTypes.longForm.item1": "Documentary films",
  "contentBriefForm.contentTypes.longForm.item2": "Brand storytelling videos",
  "contentBriefForm.contentTypes.longForm.item3": "Corporate presentations",
  "contentBriefForm.contentTypes.longForm.item4": "Event coverage",
  "contentBriefForm.contentTypes.commercial.title": "Commercial Content",
  "contentBriefForm.contentTypes.commercial.item1": "Product advertisements",
  "contentBriefForm.contentTypes.commercial.item2": "Brand campaigns",
  "contentBriefForm.contentTypes.commercial.item3": "UGC-style content",
  "contentBriefForm.contentTypes.commercial.item4": "Testimonial videos",
  "contentBriefForm.contentTypes.creative.title": "Creative Projects",
  "contentBriefForm.contentTypes.creative.item1": "Music videos",
  "contentBriefForm.contentTypes.creative.item2": "Art & culture pieces",
  "contentBriefForm.contentTypes.creative.item3": "Experimental content",
  "contentBriefForm.contentTypes.creative.item4": "Personal projects",
  "contentBriefForm.process.consultation.title": "Consultation",
  "contentBriefForm.process.consultation.description": "We discuss your vision and requirements",
  "contentBriefForm.process.planning.title": "Planning",
  "contentBriefForm.process.planning.description": "We create a detailed production plan",
  "contentBriefForm.process.production.title": "Production",
  "contentBriefForm.process.production.description": "We bring your story to life",
  "contentBriefForm.process.delivery.title": "Delivery",
  "contentBriefForm.process.delivery.description": "You receive your finished content",
  "contentBriefForm.heroBadge": "Content Creation",
  "contentBriefForm.heroTitle": "Tell It",
  "contentBriefForm.heroTitleHighlight": "With Us",
  "contentBriefForm.heroDescription": "Share your vision, your idea, or the story you've been waiting to bring to life. The more you tell us, the better we can shape it into something unforgettable.",
  "contentBriefForm.heroCta": "From concept to creation, we're here to make it happen.",
  "contentBriefForm.formTitle": "Share Your Vision",
  "contentBriefForm.formSubtitle": "Tell us about your content idea and we'll help bring it to life",
  "contentBriefForm.contentTypesTitle": "What We Can Create Together",
  "contentBriefForm.processTitle": "Our Creative Process",
  "contentBriefForm.submitButton": "Let's Begin",
  "contentBriefForm.successMessage": "Request submitted successfully! Redirecting...",
  "contentBriefForm.errorMessage": "Failed to submit. Please try again.",
  "contentBriefForm.infoMessage": "We'll review your idea and get back to you within 24 hours with a detailed proposal",
  // ── Blog Post (spotlight) ──────────────────────────────────────────────────────
  "blogPost.notFound": "Blog post not found.",
  "blogPost.backToBlogsArrow": "← Back to Blogs",
  "blogPost.backToBlogs": "Back to Blogs",
  "blogPost.minRead": "{{minutes}} min read",
  // ── Brief Details Body (shared) ────────────────────────────────────────────────
  "briefDetailsBody.projectDetails": "Project details",
  "briefDetailsBody.talentRequirements": "Talent requirements",
  "briefDetailsBody.egp": "EGP",
  "briefDetailsBody.heightCm": "Height (cm)",
  "briefDetailsBody.weightKg": "Weight (kg)",
  "briefDetailsBody.available": "Available:",
  "briefDetailsBody.selectedTalents": "Selected talents ({{count}})",
  "briefDetailsBody.unnamedTalent": "Unnamed talent",
  "briefDetailsBody.additionalNotes": "Additional notes",
  // ── Shared: misc components ────────────────────────────────────────────────────
  "errorBoundary.unexpectedError": "An unexpected error occurred",
  "errorBoundary.reloadPage": "Reload Page",
  "imageCropModal.defaultTitle": "Crop Image",
  "imageCropModal.repositionHint": "Drag to reposition · Pinch or scroll to zoom",
  "imageCropModal.processing": "Processing…",
  "imageCropModal.applyCrop": "Apply Crop",
  "nationalitySelect.selectPlaceholder": "Select Nationality",
  "offlineBanner.backOnline": "Back online",
  "offlineBanner.noConnection": "No internet connection",
  "pendingBanner.updateRejected": "Profile update rejected",
  "pendingBanner.editAndResubmit": "You can edit and resubmit your changes below.",
  "pendingBanner.pendingApproval": "Pending Approval",
  "pendingBanner.awaitingReview": "Your changes are awaiting admin review.",
  "protectedRoute.accessDenied": "Access Denied",
  "protectedRoute.noPermission": "You don't have permission to access this resource.",
  "protectedRoute.goBackHome": "Go Back Home",
  "talentCartPrompt.thisTalent": "This talent",
  "talentCartPrompt.addedToBrief": "{{name}} added to your brief",
  "talentCartPrompt.continueBrowsing": "Continue for more talents?",
  "talentCartPrompt.keepBrowsing": "Yes, keep browsing",
  "talentCartPrompt.reviewAndSubmit": "No, review & submit",
  "common.selectPlaceholder": "Select {{label}}",
  "brandLogos.title": "Brand Collaborations",
  // ── Contact ───────────────────────────────────────────────────────────────────
  "contact.title": "Contact Us",
  "contact.name": "Name",
  "contact.email": "Email",
  "contact.message": "Your Message",
  "contact.privacy": "I agree to the processing of my data in accordance with the privacy policy",
  "contact.send": "Send Message",
  'or_contact_us_directly':"Contact us directly",
  "contact.pageTitle": "Contact ARTERIA - Get In Touch",
  "contact.hero.badge": "Get In Touch",
  "contact.hero.titlePrefix": "Contact",
  "contact.hero.description": "Curious about our world of stunning talent and captivating content? We're just a message away! Reach out anytime and let's bring your vision to life.",
  "contact.form.title": "Send Us a Message",
  "contact.form.fullName": "Full Name *",
  "contact.form.fullNamePlaceholder": "Your full name",
  "contact.form.emailLabel": "Email Address *",
  "contact.form.phone": "Phone Number",
  "contact.form.messageLabel": "Message *",
  "contact.form.messagePlaceholder": "Tell us about your project, questions, or how we can help you...",
  "contact.form.sending": "Sending...",
  "contact.form.successMessage": "Your message has been sent. We'll get back to you soon.",
  "contact.form.errorMessage": "Something went wrong. Please try again.",
  "contact.info.title": "Get in Touch",
  "contact.info.description": "We're here to help bring your creative vision to life. Whether you're looking for talent, need content production, or have questions about our services, we'd love to hear from you.",
  "contact.info.phoneTitle": "Phone Numbers",
  "contact.info.primary": "Primary",
  "contact.info.secondary": "Secondary",
  "contact.info.emailTitle": "Email",
  "contact.info.generalInquiries": "General Inquiries",
  "contact.info.locationTitle": "Location",
  "contact.info.officeAddress": "Office Address",
  "contact.info.hoursTitle": "Business Hours",
  "contact.info.days.mon": "Monday",
  "contact.info.days.tue": "Tuesday",
  "contact.info.days.wed": "Wednesday",
  "contact.info.days.thu": "Thursday",
  "contact.info.days.fri": "Friday",
  "contact.info.days.sat": "Saturday",
  "contact.info.days.sun": "Sunday",
  "contact.info.closed": "Closed",
  "contact.map.title": "Visit Our Office",
  "contact.map.subtitle": "Located in the heart of {{location}} - easily accessible and ready to welcome you",
  "contact.map.interactiveMap": "Interactive Map",
  "contact.map.openInGoogleMaps": "Open in Google Maps",
  "contact.cta.titlePrefix": "Ready to Start Your",
  "contact.cta.titleHighlight": "Journey",
  "contact.cta.subtitle": "Whether you're a talent looking to join our network or a brand seeking authentic storytelling, we're here to make it happen.",
  "contact.whatsapp.ariaLabel": "Chat on WhatsApp",
  "contact.whatsapp.tooltip": "Chat with us",

  // ── Admin: Common ────────────────────────────────────────────────────────────
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.saving": "Saving...",
  "common.delete": "Delete",
  "common.deleting": "Deleting...",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.create": "Create",
  "common.update": "Update",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.actions": "Actions",
  "common.search": "Search...",
  "common.loading": "Loading...",
  "common.back": "Back",
  "common.submit": "Submit",
  "common.yes": "Yes",
  "common.no": "No",
  "common.name": "Name",
  "common.description": "Description",
  "common.status": "Status",
  "common.active": "Active",
  "common.inactive": "Inactive",
  "common.view": "View",
  "common.viewDetails": "View Details",
  "common.upload": "Upload",
  "common.required": "Required",
  "common.optional": "Optional",
  "common.noResults": "No results found",
  "common.all": "All",
  "common.retry": "Retry",
  "common.somethingWentWrong": "Something went wrong",
  "common.areYouSure": "Are you sure?",
  "common.thisActionCannotBeUndone": "This action cannot be undone.",
  "common.saveChanges": "Save Changes",
  "admin.basicInformation": "Basic Information",

  // ── Admin: Talents & Clients ─────────────────────────────────────────────────
  "toast.talent.loadFailed": "Failed to load talents",
  "toast.talent.approved": "Talent approved successfully",
  "toast.talent.approveFailed": "Failed to approve talent",
  "toast.talent.rejectionReasonRequired": "Please provide a rejection reason",
  "toast.talent.rejected": "Talent rejected",
  "toast.talent.rejectFailed": "Failed to reject talent",
  "toast.talent.deleted": "Talent deleted",
  "toast.talent.deleteFailed": "Failed to delete talent",
  "toast.talent.stageNameRequired": "Please enter a stage name",
  "toast.talent.userRequired": "Please select a user",
  "toast.talent.created": "Talent profile created",
  "toast.talent.createFailed": "Failed to create talent profile",
  "toast.client.loadFailed": "Failed to load clients",
  "toast.client.approved": "Client approved",
  "toast.client.setPending": "Client set to pending",
  "toast.client.statusUpdateFailed": "Failed to update status",
  "toast.client.deleted": "Client deleted",
  "toast.client.deleteFailed": "Failed to delete client",
  "admin.talentsManagement.badge": "Talent Hub",
  "admin.dashboard.welcomeBack": "Welcome back",
  "admin.dashboard.title": "Admin Dashboard",
  "admin.dashboard.administrator": "Administrator",
  "admin.dashboard.totalUsers": "Total Users",
  "admin.dashboard.totalTalents": "Total Talents",
  "admin.dashboard.blogs": "Blogs",
  "admin.dashboard.castingBriefs": "Casting Briefs",
  "admin.dashboard.pending": "{{count}} pending",
  "admin.dashboard.quickActions": "Quick Actions",
  "admin.dashboard.quickActionsSubtitle": "Jump into your most important admin tasks",
  "admin.dashboard.recentActivity": "Recent Activity",
  "admin.dashboard.recentActivitySubtitle": "Latest updates across your platform",
  "admin.dashboard.noRecentActivity": "No recent activity yet",
  "admin.dashboard.newTalent": "New talent: {{name}}",
  "admin.dashboard.newBlog": "New blog: {{title}}",
  "admin.dashboard.newBrief": "New brief: {{name}}",
  "admin.dashboard.newUser": "New user: {{name}}",
  "admin.dashboard.unnamed": "Unnamed",
  "admin.dashboard.untitled": "Untitled",
  "admin.dashboard.unknown": "Unknown",
  "admin.dashboard.users": "Users",
  "admin.dashboard.usersDescription": "Manage all platform users",
  "admin.dashboard.talents": "Talents",
  "admin.dashboard.talentsDescription": "Approve or reject talents",
  "admin.dashboard.clients": "Clients",
  "admin.dashboard.clientsDescription": "View and manage client accounts",
  "admin.dashboard.blog": "Blog",
  "admin.dashboard.blogDescription": "Create and publish articles",
  "admin.dashboard.briefs": "Briefs",
  "admin.dashboard.briefsDescription": "Review casting requests",
  "admin.dashboard.categories": "Categories",
  "admin.dashboard.categoriesDescription": "Organize platform categories",
  "admin.dashboard.works": "Works",
  "admin.dashboard.worksDescription": "Manage portfolios & works",
  "admin.dashboard.albums": "Albums",
  "admin.dashboard.albumsDescription": "Manage homepage gallery images",
  "admin.dashboard.content": "Content",
  "admin.dashboard.contentDescription": "Manage platform content",
  "admin.dashboard.specialties": "Specialties",
  "admin.dashboard.specialtiesDescription": "Manage talent specialties",
  "admin.dashboard.questions": "Questions",
  "admin.dashboard.questionsDescription": "Manage registration questions",
  "admin.dashboard.profileUpdates": "Profile Updates",
  "admin.dashboard.profileUpdatesDescription": "Review pending profile changes",
  "admin.dashboard.quotations": "Quotations",
  "admin.dashboard.quotationsDescription": "Manage quotations",
  "admin.dashboard.contactMessages": "Contact Messages",
  "admin.dashboard.contactMessagesDescription": "View messages sent from the contact page",
  "admin.dashboard.trustedBy": "Trusted By",
  "admin.dashboard.trustedByDescription": "Manage trusted brand logos",
  "admin.dashboard.projectTypes": "Project Types",
  "admin.dashboard.projectTypesDescription": "Manage project type classifications",
  "admin.dashboard.aboutSection": "About Section",
  "admin.dashboard.aboutSectionDescription": "Update homepage about image & text",
  "admin.dashboard.footerSettings": "Footer Settings",
  "admin.dashboard.footerSettingsDescription": "Manage contact info & social links",
  "admin.dashboard.testimonials": "Testimonials",
  "admin.dashboard.testimonialsDescription": "Manage client testimonials",
  "admin.briefsManagement.title": "Casting Briefs",
  "admin.briefsManagement.subtitle": "Review and manage casting requests",
  "admin.briefsManagement.totalBriefs": "Total briefs",
  "admin.briefsManagement.stats.pending": "Pending",
  "admin.briefsManagement.stats.inReview": "In review",
  "admin.briefsManagement.stats.approved": "Approved",
  "admin.briefsManagement.stats.completed": "Completed",
  "admin.briefsManagement.searchPlaceholder": "Search briefs...",
  "admin.briefsManagement.filter.allStatus": "All statuses",
  "admin.briefsManagement.filter.pending": "Pending",
  "admin.briefsManagement.filter.inReview": "In review",
  "admin.briefsManagement.filter.approved": "Approved",
  "admin.briefsManagement.filter.rejected": "Rejected",
  "admin.briefsManagement.filter.completed": "Completed",
  "admin.briefsManagement.empty.title": "No briefs found",
  "admin.briefsManagement.empty.subtitle": "There are no casting briefs matching your filters",
  "admin.briefsManagement.untitledProject": "Untitled project",
  "admin.briefsManagement.unknownClient": "Unknown client",
  "admin.briefsManagement.priorityLabel": "Priority",
  "admin.briefsManagement.talentsSelected": "{{count}} talents selected",
  "admin.talentsManagement.title": "Talent Management",
  "admin.talentsManagement.subtitle": "Review and manage talent applications",
  "admin.talentsManagement.stats.pendingReview": "Pending Review",
  "admin.talentsManagement.stats.awaitingReview": "Awaiting review",
  "admin.talentsManagement.stats.approved": "Approved",
  "admin.talentsManagement.stats.activeTalents": "Active talents",
  "admin.talentsManagement.stats.rejected": "Rejected",
  "admin.talentsManagement.stats.notApproved": "Not approved",
  "admin.talentsManagement.filters.title": "Filters",
  "admin.talentsManagement.filters.searchPlaceholder": "Search by stage name or email...",
  "admin.talentsManagement.filters.statusAriaLabel": "Filter by status",
  "admin.talentsManagement.filters.allStatus": "All Status",
  "admin.talentsManagement.filters.pending": "Pending",
  "admin.talentsManagement.filters.approved": "Approved",
  "admin.talentsManagement.filters.rejected": "Rejected",
  "admin.talentsManagement.empty.title": "No talents found",
  "admin.talentsManagement.empty.subtitle": "Start by adding a new talent or adjust your filters",
  "admin.talentsManagement.pagination.showing": "Showing",
  "admin.talentsManagement.pagination.of": "of",
  "admin.talentsManagement.pagination.talents": "talents",
  "admin.talentsManagement.pagination.prev": "Prev",
  "admin.talentsManagement.pagination.page": "Page",
  "admin.talentsManagement.pagination.next": "Next",
  "admin.talentCard.defaultAlt": "Talent",
  "admin.talentCard.unnamedTalent": "Unnamed Talent",
  "admin.talentCard.dob": "DOB",
  "admin.talentCard.gender": "Gender",
  "admin.talentCard.experience": "Experience",
  "admin.talentCard.years": "years",
  "admin.talentCard.categories": "Categories",
  "admin.talentCard.approve": "Approve",
  "admin.talentDetailsModal.title": "Talent Profile",
  "admin.talentDetailsModal.fullName": "Full Name",
  "admin.talentDetailsModal.codeName": "Code Name",
  "admin.talentDetailsModal.email": "Email",
  "admin.talentDetailsModal.phone": "Phone",
  "admin.talentDetailsModal.talentType": "Talent Type",
  "admin.talentDetailsModal.memberSince": "Member Since",
  "admin.talentDetailsModal.approvedAt": "Approved At",
  "admin.talentDetailsModal.personalInformation": "Personal Information",
  "admin.talentDetailsModal.age": "Age",
  "admin.talentDetailsModal.ageValue": "{{age}} years",
  "admin.talentDetailsModal.dateOfBirth": "Date of Birth",
  "admin.talentDetailsModal.gender": "Gender",
  "admin.talentDetailsModal.nationality": "Nationality",
  "admin.talentDetailsModal.city": "City",
  "admin.talentDetailsModal.physicalAttributes": "Physical Attributes",
  "admin.talentDetailsModal.height": "Height",
  "admin.talentDetailsModal.heightValue": "{{height}} cm",
  "admin.talentDetailsModal.weight": "Weight",
  "admin.talentDetailsModal.weightValue": "{{weight}} kg",
  "admin.talentDetailsModal.eyeColor": "Eye Color",
  "admin.talentDetailsModal.hairColor": "Hair Color",
  "admin.talentDetailsModal.hairType": "Hair Type",
  "admin.talentDetailsModal.skinTone": "Skin Tone",
  "admin.talentDetailsModal.bodyType": "Body Type",
  "admin.talentDetailsModal.professional": "Professional",
  "admin.talentDetailsModal.experience": "Experience",
  "admin.talentDetailsModal.experienceYear": "{{count}} year",
  "admin.talentDetailsModal.experienceYears": "{{count}} years",
  "admin.talentDetailsModal.rate": "Rate",
  "admin.talentDetailsModal.socialMediaAdminOnly": "Social Media (Admin Only)",
  "admin.talentDetailsModal.instagram": "Instagram",
  "admin.talentDetailsModal.facebook": "Facebook",
  "admin.talentDetailsModal.tiktok": "TikTok",
  "admin.talentDetailsModal.skillsAndCategories": "Skills & Categories",
  "admin.talentDetailsModal.categories": "Categories",
  "admin.talentDetailsModal.skills": "Skills",
  "admin.talentDetailsModal.languages": "Languages",
  "admin.talentDetailsModal.specialties": "Specialties",
  "admin.talentDetailsModal.bio": "Bio",
  "admin.talentDetailsModal.portfolioDescription": "Portfolio Description",
  "admin.talentDetailsModal.portfolioImages": "Portfolio Images",
  "admin.talentDetailsModal.portfolioImageAlt": "Portfolio {{index}}",
  "admin.talentDetailsModal.portfolioVideos": "Portfolio Videos",
  "admin.talentDetailsModal.videoLabel": "Video {{index}}",
  "admin.talentDetailsModal.rejectionReason": "Rejection Reason",
  "admin.talentDetailsModal.rejectionReasonOptional": "Rejection Reason (optional)",
  "admin.talentDetailsModal.rejectionReasonPlaceholder": "Enter reason for rejection...",
  "admin.talentDetailsModal.confirmDelete": "Delete this talent?",
  "admin.talentDetailsModal.editProfile": "Edit Profile",
  "admin.talentDetailsModal.reject": "Reject",
  "admin.talentDetailsModal.approve": "Approve",
  "admin.clientsManagement.badge": "Client Hub",
  "admin.clientsManagement.title": "Client Management",
  "admin.clientsManagement.subtitle": "View and manage registered client accounts",
  "admin.clientsManagement.stats.totalClients": "Total Clients",
  "admin.clientsManagement.searchPlaceholder": "Search by name or email...",
  "admin.clientsManagement.empty.title": "No clients found",
  "admin.clientsManagement.empty.subtitle": "Adjust your search and try again",
  "admin.clientsManagement.pagination.showing": "Showing",
  "admin.clientsManagement.pagination.of": "of",
  "admin.clientsManagement.pagination.clients": "clients",
  "admin.clientsManagement.pagination.prev": "Prev",
  "admin.clientsManagement.pagination.page": "Page",
  "admin.clientsManagement.pagination.next": "Next",
  "admin.clientDetailsModal.joined": "Joined {{date}}",
  "admin.clientDetailsModal.email": "Email",
  "admin.clientDetailsModal.phone": "Phone",
  "admin.clientDetailsModal.company": "Company",
  "admin.clientDetailsModal.companyType": "Company Type",
  "admin.clientDetailsModal.title": "Title",
  "admin.clientDetailsModal.whatsapp": "WhatsApp",
  "admin.clientDetailsModal.editProfile": "Edit profile",

  // ── Admin: Quotation Details Modal ────────────────────────────────────────
  "admin.quotationDetailsModal.email": "Email",
  "admin.quotationDetailsModal.phone": "Phone",
  "admin.quotationDetailsModal.idea": "Idea",
  "admin.quotationDetailsModal.equipment": "Equipment",
  "admin.quotationDetailsModal.equipmentProduction": "Equipment / Production Needs",
  "admin.quotationDetailsModal.submitted": "Submitted on {{date}}",
  "admin.quotationDetailsModal.deleteTitle": "Delete this quotation?",
  "admin.quotationDetailsModal.actions.approve": "Approve",
  "admin.quotationDetailsModal.actions.pending": "Mark Pending",
  "admin.quotationDetailsModal.actions.reject": "Reject",

  // ── Admin: User Modal ──────────────────────────────────────────────────────
  "admin.userModal.talentTypeModeling": "Modeling",
  "admin.userModal.talentTypeContentCreator": "Content Creator",
  "admin.userModal.talentTypeBoth": "Both",
  "admin.userModal.addTitle": "Add New User",
  "admin.userModal.firstNameLabel": "First Name *",
  "admin.userModal.firstNamePlaceholder": "First name",
  "admin.userModal.lastNameLabel": "Last Name *",
  "admin.userModal.lastNamePlaceholder": "Last name",
  "admin.userModal.emailLabel": "Email *",
  "admin.userModal.roleLabel": "Role *",
  "admin.userModal.roleClient": "Client",
  "admin.userModal.roleTalent": "Talent",
  "admin.userModal.roleAdmin": "Admin",
  "admin.userModal.passwordLabel": "Password *",
  "admin.userModal.confirmPasswordLabel": "Confirm Password *",
  "admin.userModal.clientInformation": "Client Information",
  "admin.userModal.companyLabel": "Company *",
  "admin.userModal.companyPlaceholder": "Company name",
  "admin.userModal.companyTypeLabel": "Company Type",
  "admin.userModal.companyTypePlaceholder": "e.g. Agency, Brand, Production House",
  "admin.userModal.titleLabel": "Title",
  "admin.userModal.titlePlaceholder": "e.g. CEO, Marketing Manager",
  "admin.userModal.talentTypeSection": "Talent Type",
  "admin.userModal.talentTypeLabel": "Talent Type *",
  "admin.userModal.dobLabel": "Date of Birth *",
  "admin.userModal.contentCreatorTypesLabel": "Content Creator Types *",
  "admin.userModal.loadingTypes": "Loading types...",
  "admin.userModal.cityLabel": "City",
  "admin.userModal.genderLabel": "Gender *",
  "admin.userModal.specialtiesLabel": "Specialties *",
  "admin.userModal.experienceLevelLabel": "Experience Level *",
  "admin.userModal.physicalDetails": "Physical Details",
  "admin.userModal.heightLabel": "Height (cm)",
  "admin.userModal.weightLabel": "Weight (kg)",
  "admin.userModal.tshirtLabel": "T-Shirt",
  "admin.userModal.pantLabel": "Pant",
  "admin.userModal.shoeSizeLabel": "Shoe Size",
  "admin.userModal.skinToneLabel": "Skin Tone",
  "admin.userModal.hairTypeLabel": "Hair Type",
  "admin.userModal.eyeColorLabel": "Eye Color",
  "admin.userModal.hairColorLabel": "Hair Color",
  "admin.userModal.bodyTypeLabel": "Body Type",
  "admin.userModal.bioLabel": "Bio",
  "admin.userModal.bioPlaceholder": "Short bio...",
  "admin.userModal.ratesLabel": "Rates (EGP / 12h)",
  "admin.userModal.ratesPlaceholder": "e.g. 500",
  "admin.userModal.createUserButton": "Create User",
  "admin.userModal.whatsappLabel": "WhatsApp Number",

  // ── Admin: Album Modal ─────────────────────────────────────────────────────
  "admin.albumModal.addImage": "Add Image",
  "admin.albumModal.addImagesCount": "Add {{count}} Images",
  "admin.albumModal.addMore": "Add More",
  "admin.albumModal.browse": "Browse",
  "admin.albumModal.change": "Change",
  "admin.albumModal.closeModal": "Close",
  "admin.albumModal.cropTitle": "Crop Image",
  "admin.albumModal.cropTitleWithMore": "Crop Image (+{{count}} more)",
  "admin.albumModal.currentImage": "Current Image",
  "admin.albumModal.editAlbumImage": "Edit Album Image",
  "admin.albumModal.image": "Image",
  "admin.albumModal.imageTooLarge": "{{name}} is too large (max {{maxMb}}MB)",
  "admin.albumModal.images": "Images",
  "admin.albumModal.multiplePhotosHint": "You can select multiple photos at once",
  "admin.albumModal.newAlbumImages": "New Album Images",
  "admin.albumModal.noFileSelected": "No file selected",
  "admin.albumModal.noFilesSelected": "No files selected",
  "admin.albumModal.orderLabel": "Order",
  "admin.albumModal.photosSelected": "{{count}} photos selected",
  "admin.albumModal.processing": "Processing...",
  "admin.albumModal.removeFile": "Remove File",
  "admin.albumModal.removeImageAt": "Remove image",
  "admin.albumModal.replace": "Replace",
  "admin.albumModal.saveChanges": "Save Changes",
  "admin.albumModal.startingOrderHint": "Starting position in the album order",
  "admin.albumModal.titleLabel": "Title",
  "admin.albumModal.titlePlaceholder": "Enter a title",
  "admin.albumModal.updateDetails": "Update Details",
  "admin.albumModal.uploadImagesHint": "Drag and drop images here, or click to browse",
  "admin.albumModal.uploading": "Uploading...",
  "admin.albumModal.uploadingPhotos": "Uploading photos...",

  // ── Admin: Albums Management ───────────────────────────────────────────────
  "admin.albumsManagement.addFirstImage": "Add First Image",
  "admin.albumsManagement.addImage": "Add Image",
  "admin.albumsManagement.addNewImage": "Add New Image",
  "admin.albumsManagement.clear": "Clear",
  "admin.albumsManagement.deleteAlbumImage": "Delete this album image?",
  "admin.albumsManagement.editAlbumImage": "Edit Album Image",
  "admin.albumsManagement.galleryHub": "Gallery Hub",
  "admin.albumsManagement.noAlbumsFound": "No albums found",
  "admin.albumsManagement.order": "Order",
  "admin.albumsManagement.searchPlaceholder": "Search albums...",
  "admin.albumsManagement.subtitle": "Manage photo albums and galleries",
  "admin.albumsManagement.title": "Albums Management",
  "admin.albumsManagement.totalImages": "Total Images",
  "admin.albumsManagement.untitled": "Untitled",

  // ── Admin: Blog Editor ─────────────────────────────────────────────────────
  "admin.blogEditor.createTitle": "Create Blog Post",
  "admin.blogEditor.editTitle": "Edit Blog Post",
  "admin.blogEditor.label": "Blog Post",
  "admin.blogEditor.subtitle": "Write and publish spotlight content",
  "admin.blogEditor.titleLabel": "Title *",
  "admin.blogEditor.titlePlaceholder": "Enter blog title",
  "admin.blogEditor.slugLabel": "Slug",
  "admin.blogEditor.slugAutoGenerated": "Auto-generated from title",
  "admin.blogEditor.categoryLabel": "Category *",
  "admin.blogEditor.selectCategory": "Select a category",
  "admin.blogEditor.statusLabel": "Status *",
  "admin.blogEditor.coverImageSection": "Cover Image",
  "admin.blogEditor.coverImageFormat": "JPG, PNG · recommended 16:9",
  "admin.blogEditor.changeImage": "Change image",
  "admin.blogEditor.removeCoverImage": "Remove cover image",
  "admin.blogEditor.contentSection": "Content",
  "admin.blogEditor.contentLabel": "Content *",
  "admin.blogEditor.introOptional": "(optional)",
  "admin.blogEditor.introPlaceholder": "A brief summary of your blog post...",
  "admin.blogEditor.introHint": "Shown in search results and feeds",
  "admin.blogEditor.markdownHint": "Supports markdown formatting",
  "admin.blogEditor.tagsMetadataSection": "Tags & Metadata",
  "admin.blogEditor.updateBlogButton": "Update Blog",
  "admin.blogEditor.publishBlogButton": "Publish Blog",
  "admin.blogEditor.cropCoverTitle": "Crop Cover Image",

  // ── Admin: About Section Settings ──────────────────────────────────────────
  "admin.aboutSectionSettings.title": "About Section Image",
  "admin.aboutSectionSettings.subtitle": "Manage the image and card text shown in the About section on the homepage.",
  "admin.aboutSectionSettings.sectionImageLabel": "Section Image",
  "admin.aboutSectionSettings.clickToUploadImage": "Click to upload image",
  "admin.aboutSectionSettings.changeImage": "Change image",
  "admin.aboutSectionSettings.recommendedRatio": "Recommended: portrait ratio (4:5), at least 800×1000px",
  "admin.aboutSectionSettings.cardTitleLabel": "Card Title",
  "admin.aboutSectionSettings.cardSubtitleLabel": "Card Subtitle",
  "admin.aboutSectionSettings.previewLabel": "Preview",
  "admin.aboutSectionSettings.cropTitle": "Crop About Section Image",

  // ── Admin: Footer Settings ─────────────────────────────────────────────────
  "admin.footerSettings.title": "Footer Settings",
  "admin.footerSettings.subtitle": "Manage the contact info and social links shown in the site footer.",
  "admin.footerSettings.scopeNote": "This information also powers the \"Get in Touch\" section on the Contact page — update it once here.",
  "admin.footerSettings.contactSection": "Contact Info",
  "admin.footerSettings.emailLabel": "Email",
  "admin.footerSettings.phoneLabel": "Primary Phone",
  "admin.footerSettings.secondaryPhoneLabel": "Secondary Phone",
  "admin.footerSettings.mapUrlLabel": "Location on Map",
  "admin.footerSettings.addressLabel": "Office Address",
  "admin.footerSettings.addressHint": "This is the address text shown in the footer and Contact page — the map pin above only sets the map link.",
  "admin.footerSettings.hoursSection": "Business Hours",
  "admin.footerSettings.businessHoursHint": "Check the days you're open and enter the hours for each. Unchecked days show as \"Closed\".",
  "admin.footerSettings.days.mon": "Monday",
  "admin.footerSettings.days.tue": "Tuesday",
  "admin.footerSettings.days.wed": "Wednesday",
  "admin.footerSettings.days.thu": "Thursday",
  "admin.footerSettings.days.fri": "Friday",
  "admin.footerSettings.days.sat": "Saturday",
  "admin.footerSettings.days.sun": "Sunday",
  "admin.footerSettings.socialSection": "Social Links",
  "admin.footerSettings.socialHint": "Leave a field empty to show it as \"coming soon\" instead of a link.",

  // ── Admin: Location Picker ─────────────────────────────────────────────────
  "admin.locationPicker.searchPlaceholder": "Search for an address...",
  "admin.locationPicker.searchButton": "Search",
  "admin.locationPicker.searching": "Searching...",
  "admin.locationPicker.noResults": "No location found for that search.",
  "admin.locationPicker.searchFailed": "Search failed. Please try again.",
  "admin.locationPicker.hint": "Click on the map or drag the pin to set the exact location shown in the footer.",

  // ── Admin: Categories Management ───────────────────────────────────────────
  "admin.categoriesManagement.contentHubBadge": "Content Hub",
  "admin.categoriesManagement.title": "Blogs Categories Management",
  "admin.categoriesManagement.subtitle": "Organize and manage all content categories",
  "admin.categoriesManagement.createButton": "Create Category",
  "admin.categoriesManagement.statTotal": "Total Categories",
  "admin.categoriesManagement.statResults": "Search Results",
  "admin.categoriesManagement.statEmpty": "Empty Categories",
  "admin.categoriesManagement.searchPlaceholder": "Search categories...",
  "admin.categoriesManagement.noneFound": "No categories found",
  "admin.categoriesManagement.startCreating": "Start by creating a new category",
  "admin.categoriesManagement.slugLabel": "Slug",
  "admin.categoriesManagement.createdLabel": "Created",
  "admin.categoriesManagement.deleteConfirmTitle": "Delete category?",

  // ── Admin: Category Modal ──────────────────────────────────────────────────
  "admin.categoryModal.label": "Category",
  "admin.categoryModal.editTitle": "Edit Category",
  "admin.categoryModal.nameLabel": "Category Name *",
  "admin.categoryModal.namePlaceholder": "Enter category name...",

  // ── Admin: Testimonials Management ─────────────────────────────────────────
  "admin.testimonialsManagement.badge": "Client Reviews",
  "admin.testimonialsManagement.title": "Testimonials",
  "admin.testimonialsManagement.subtitle": "Manage client testimonials shown on the Work page",
  "admin.testimonialsManagement.addButton": "Add Testimonial",
  "admin.testimonialsManagement.statTotal": "Total Testimonials",
  "admin.testimonialsManagement.searchPlaceholder": "Search by name, company or project...",
  "admin.testimonialsManagement.noneFound": "No testimonials found",
  "admin.testimonialsManagement.addFirst": "Add the first client testimonial",
  "admin.testimonialsManagement.projectLabel": "Project",
  "admin.testimonialsManagement.deleteConfirmTitle": "Delete testimonial?",

  // ── Admin: Testimonial Modal ───────────────────────────────────────────────
  "admin.testimonialModal.editTitle": "Edit Testimonial",
  "admin.testimonialModal.nameLabel": "Name *",
  "admin.testimonialModal.jobTitleLabel": "Job Title *",
  "admin.testimonialModal.companyLabel": "Company *",
  "admin.testimonialModal.projectLabel": "Project *",
  "admin.testimonialModal.quoteLabel": "Quote *",
  "admin.testimonialModal.quotePlaceholder": "What did the client say about working with ARTERIA?",
  "admin.testimonialModal.ratingLabel": "Rating",
  "admin.testimonialModal.updateButton": "Update",

  // ── Admin: Profile Update Requests ─────────────────────────────────────────
  "admin.profileUpdateRequests.title": "Profile Update Requests",
  "admin.profileUpdateRequests.pendingBadge": "{{count}} pending",
  "admin.profileUpdateRequests.noneFound": "No pending profile update requests",
  "admin.profileUpdateRequests.fieldsChangedOne": "{{count}} field changed · {{date}}",
  "admin.profileUpdateRequests.fieldsChangedOther": "{{count}} fields changed · {{date}}",
  "admin.profileUpdateRequests.reviewButton": "Review",

  // ── Admin: Profile Update Compare ──────────────────────────────────────────
  "admin.profileUpdateCompare.title": "Profile Update Review",
  "admin.profileUpdateCompare.changeOne": "{{count}} change",
  "admin.profileUpdateCompare.changeOther": "{{count}} changes",
  "admin.profileUpdateCompare.noChangedFields": "No changed fields found in this request.",
  "admin.profileUpdateCompare.profileImageLabel": "Profile Image",
  "admin.profileUpdateCompare.changedBadge": "Changed",
  "admin.profileUpdateCompare.beforeLabel": "Before",
  "admin.profileUpdateCompare.afterLabel": "After",
  "admin.profileUpdateCompare.noImage": "No image",
  "admin.profileUpdateCompare.rejectionReasonLabel": "Rejection Reason",
  "admin.profileUpdateCompare.rejectionReasonPlaceholder": "Explain why this update is rejected (minimum {{min}} characters)...",
  "admin.profileUpdateCompare.requiredField": "Required field",
  "admin.profileUpdateCompare.readyToSubmit": "✓ Ready to submit",
  "admin.profileUpdateCompare.moreCharactersNeeded": "{{count}} more characters needed",
  "admin.profileUpdateCompare.rejectButton": "Reject",
  "admin.profileUpdateCompare.approveButton": "Approve",
  "admin.profileUpdateCompare.confirmReject": "Confirm Reject",

  // ── Admin: Blogs Management ────────────────────────────────────────────────
  "admin.blogsManagement.approveTitle": "Approve this blog post?",
  "admin.blogsManagement.clearFilters": "Clear Filters",
  "admin.blogsManagement.clearFiltersButton": "Clear Filters",
  "admin.blogsManagement.colBlogPost": "Blog Post",
  "admin.blogsManagement.colCategory": "Category",
  "admin.blogsManagement.colCreated": "Created",
  "admin.blogsManagement.createFirstBlog": "Create your first blog post",
  "admin.blogsManagement.deleteConfirmTitle": "Delete this blog post?",
  "admin.blogsManagement.filterActive": "Active",
  "admin.blogsManagement.filtersHeading": "Filters",
  "admin.blogsManagement.itemsOption": "{{count}} items",
  "admin.blogsManagement.newBlogPost": "New Blog Post",
  "admin.blogsManagement.next": "Next",
  "admin.blogsManagement.noBlogsFound": "No blog posts found",
  "admin.blogsManagement.noResultsFilters": "No results match your filters",
  "admin.blogsManagement.ofLabel": "of",
  "admin.blogsManagement.pageLabel": "Page",
  "admin.blogsManagement.pendingLabel": "Pending",
  "admin.blogsManagement.pendingSub": "Awaiting review",
  "admin.blogsManagement.perPageLabel": "Per page",
  "admin.blogsManagement.previous": "Previous",
  "admin.blogsManagement.publishedLabel": "Published",
  "admin.blogsManagement.publishedSub": "Live on the site",
  "admin.blogsManagement.rejectTitle": "Reject this blog post?",
  "admin.blogsManagement.rejectedLabel": "Rejected",
  "admin.blogsManagement.rejectedSub": "Not published",
  "admin.blogsManagement.searchBlogsLabel": "Search blogs",
  "admin.blogsManagement.searchPlaceholder": "Search blog posts...",
  "admin.blogsManagement.showingLabel": "Showing",
  "admin.blogsManagement.startCreating": "Start creating content",
  "admin.blogsManagement.statusAll": "All",
  "admin.blogsManagement.statusApproved": "Approved",
  "admin.blogsManagement.statusPending": "Pending",
  "admin.blogsManagement.statusRejected": "Rejected",
  "admin.blogsManagement.subtitle": "Manage blog posts and spotlight articles",
  "admin.blogsManagement.title": "Blogs Management",
  "admin.blogsManagement.viewTitle": "View Blog Post",

  // ── Admin: Brief Details Modal ─────────────────────────────────────────────
  "admin.briefDetailsModal.clientInformation": "Client Information",
  "admin.briefDetailsModal.currentWorkflowStatus": "Current Workflow Status",
  "admin.briefDetailsModal.email": "Email",
  "admin.briefDetailsModal.phone": "Phone",
  "admin.briefDetailsModal.quickActions": "Quick Actions",
  "admin.briefDetailsModal.title": "Casting Brief Details",
  "admin.briefDetailsModal.updating": "Updating...",
  "admin.briefDetailsModal.urgencyLevel": "Urgency Level",
  "admin.briefDetailsModal.actions.inReview": "Mark In Review",
  "admin.briefDetailsModal.actions.approve": "Approve",
  "admin.briefDetailsModal.actions.reject": "Reject",
  "admin.briefDetailsModal.actions.complete": "Mark Completed",

  // ── Admin: Content Edit Modal ──────────────────────────────────────────────
  "admin.contentEditModal.bothVideoWarning": "You can only use one video source — remove one before saving",
  "admin.contentEditModal.categoryLabel": "Category",
  "admin.contentEditModal.categoryPlaceholder": "e.g. Trending, Featured",
  "admin.contentEditModal.charsCount": "{{count}} characters",
  "admin.contentEditModal.clickOrDrag": "Click or drag a video file here",
  "admin.contentEditModal.createTitle": "Add Content",
  "admin.contentEditModal.currentVideo": "Current Video",
  "admin.contentEditModal.descriptionLabel": "Description",
  "admin.contentEditModal.descriptionPlaceholder": "Write a short description...",
  "admin.contentEditModal.discard": "Discard",
  "admin.contentEditModal.discardChangesText": "You have unsaved changes. Are you sure you want to discard them?",
  "admin.contentEditModal.discardChangesTitle": "Discard changes?",
  "admin.contentEditModal.dropItHere": "Drop it here",
  "admin.contentEditModal.editTitle": "Edit Content",
  "admin.contentEditModal.externalUrlLabel": "External Video URL",
  "admin.contentEditModal.fillDetails": "Fill in the details below",
  "admin.contentEditModal.idLabel": "ID: {{id}}",
  "admin.contentEditModal.keepEditing": "Keep Editing",
  "admin.contentEditModal.metricLikes": "Likes",
  "admin.contentEditModal.metricShares": "Shares",
  "admin.contentEditModal.metricViews": "Views",
  "admin.contentEditModal.metrics": "Metrics",
  "admin.contentEditModal.newUpload": "New upload",
  "admin.contentEditModal.remove": "Remove",
  "admin.contentEditModal.saveChanges": "Save Changes",
  "admin.contentEditModal.titleLabel": "Title",
  "admin.contentEditModal.titlePlaceholder": "Enter a title",
  "admin.contentEditModal.uploadComplete": "Upload complete",
  "admin.contentEditModal.uploadFailedLabel": "Upload failed",
  "admin.contentEditModal.uploadFileLabel": "Upload a file",
  "admin.contentEditModal.uploadingVideo": "Uploading video...",
  "admin.contentEditModal.videoFormatsHint": "Supported formats: MP4, WebM, MOV",
  "admin.contentEditModal.videoSources": "Video Sources",
  "admin.contentEditModal.videoTooLarge": "Video file is too large",
  "admin.contentEditModal.videoUrlPlaceholder": "https://...",

  // ── Admin: Contents Management ─────────────────────────────────────────────
  "admin.contentsManagement.addContent": "Add Content",
  "admin.contentsManagement.addFirstContent": "Add your first content",
  "admin.contentsManagement.clickToPlay": "Click to play",
  "admin.contentsManagement.deleteConfirmTitle": "Delete this content?",
  "admin.contentsManagement.likes": "Likes",
  "admin.contentsManagement.noContentFound": "No content found",
  "admin.contentsManagement.noVideo": "No video",
  "admin.contentsManagement.searchPlaceholder": "Search content...",
  "admin.contentsManagement.shares": "Shares",
  "admin.contentsManagement.startAdding": "Start adding content",
  "admin.contentsManagement.subtitle": "Manage videos and media content",
  "admin.contentsManagement.title": "Content Management",
  "admin.contentsManagement.tryAdjustingSearch": "Try adjusting your search",
  "admin.contentsManagement.views": "Views",

  // ── Admin: Contents New ────────────────────────────────────────────────────
  "admin.contentsNew.backToContent": "Back to Content",
  "admin.contentsNew.basicInformation": "Basic Information",
  "admin.contentsNew.categoryLabel": "Category",
  "admin.contentsNew.createContentTitle": "Create Content",
  "admin.contentsNew.descriptionLabel": "Description",
  "admin.contentsNew.descriptionPlaceholder": "Write a short description...",
  "admin.contentsNew.dropOrBrowse": "Drop a file here, or click to browse",
  "admin.contentsNew.dropToUpload": "Drop to upload",
  "admin.contentsNew.editContentTitle": "Edit Content",
  "admin.contentsNew.editingLabel": "Editing",
  "admin.contentsNew.externalVideoUrlLabel": "External Video URL",
  "admin.contentsNew.fillRequiredFields": "Please fill in the required fields",
  "admin.contentsNew.likes": "Likes",
  "admin.contentsNew.media": "Media",
  "admin.contentsNew.metrics": "Metrics",
  "admin.contentsNew.newContentLabel": "New Content",
  "admin.contentsNew.orUseUrl": "Or use a URL instead",
  "admin.contentsNew.publishContent": "Publish Content",
  "admin.contentsNew.publishing": "Publishing...",
  "admin.contentsNew.readyToPublish": "Ready to publish",
  "admin.contentsNew.removeVideo": "Remove Video",
  "admin.contentsNew.removeVideoToUseUrl": "Remove the uploaded video to use a URL instead",
  "admin.contentsNew.selectCategory": "Select a category",
  "admin.contentsNew.selectedVideoFallback": "Selected video",
  "admin.contentsNew.shares": "Shares",
  "admin.contentsNew.titleLabel": "Title",
  "admin.contentsNew.titlePlaceholder": "Enter a title",
  "admin.contentsNew.tryDifferentFile": "Try a different file",
  "admin.contentsNew.updateContent": "Update Content",
  "admin.contentsNew.updating": "Updating...",
  "admin.contentsNew.uploadFailedDefault": "Upload failed. Please try again.",
  "admin.contentsNew.uploadFailedLabel": "Upload failed",
  "admin.contentsNew.uploadVideo": "Upload Video",
  "admin.contentsNew.uploadingVideo": "Uploading video...",
  "admin.contentsNew.videoFormatsHint": "Supported formats: MP4, WebM, MOV",
  "admin.contentsNew.videoTooLarge": "Video file is too large",
  "admin.contentsNew.videoUploadedSuccess": "Video uploaded successfully",
  "admin.contentsNew.videoUrlPlaceholder": "https://...",
  "admin.contentsNew.views": "Views",

  // ── Admin: Edit User ────────────────────────────────────────────────────────
  "admin.editUser.addLanguage": "Add Language",
  "admin.editUser.basicInformation": "Basic Information",
  "admin.editUser.bio": "Bio",
  "admin.editUser.bioPlaceholder": "Write a short bio...",
  "admin.editUser.bodyType": "Body Type",
  "admin.editUser.changePhoto": "Change Photo",
  "admin.editUser.city": "City",
  "admin.editUser.clientInformation": "Client Information",
  "admin.editUser.clientTitle": "Client",
  "admin.editUser.codeName": "Code Name",
  "admin.editUser.company": "Company",
  "admin.editUser.companyType": "Company Type",
  "admin.editUser.dateOfBirth": "Date of Birth",
  "admin.editUser.experienceYears": "Experience (years)",
  "admin.editUser.eyeColor": "Eye Color",
  "admin.editUser.female": "Female",
  "admin.editUser.gallery": "Gallery",
  "admin.editUser.gender": "Gender",
  "admin.editUser.hairColor": "Hair Color",
  "admin.editUser.hairType": "Hair Type",
  "admin.editUser.heightCm": "Height (cm)",
  "admin.editUser.imageHint": "JPG, PNG or WebP — max 5MB",
  "admin.editUser.languages": "Languages",
  "admin.editUser.lookAlike": "Look Alike",
  "admin.editUser.male": "Male",
  "admin.editUser.noImage": "No Image",
  "admin.editUser.otherLanguage": "Other Language",
  "admin.editUser.pantSize": "Pant Size",
  "admin.editUser.phone": "Phone",
  "admin.editUser.physicalAttributes": "Physical Attributes",
  "admin.editUser.profileEnlarged": "Enlarged profile photo",
  "admin.editUser.profileImage": "Profile Image",
  "admin.editUser.ratesPer": "Rates Per",
  "admin.editUser.removeLanguage": "Remove Language",
  "admin.editUser.saveChanges": "Save Changes",
  "admin.editUser.selectField": "Select...",
  "admin.editUser.selectGender": "Select Gender",
  "admin.editUser.selectTalentType": "Select Talent Type",
  "admin.editUser.shoeSize": "Shoe Size",
  "admin.editUser.skinTone": "Skin Tone",
  "admin.editUser.specialization": "Specialization",
  "admin.editUser.specialties": "Specialties",
  "admin.editUser.specifyLanguage": "Please specify the language",
  "admin.editUser.statusApproved": "Approved",
  "admin.editUser.statusPending": "Pending",
  "admin.editUser.statusRejected": "Rejected",
  "admin.editUser.tShirtSize": "T-Shirt Size",
  "admin.editUser.talentDetails": "Talent Details",
  "admin.editUser.talentType": "Talent Type",
  "admin.editUser.title": "Edit User",
  "admin.editUser.weightKg": "Weight (kg)",
  "admin.editUser.whatsappNumber": "WhatsApp Number",

  // ── Admin: Gallery ─────────────────────────────────────────────────────────
  "admin.gallery.addPhotos": "Add Photos",
  "admin.gallery.confirmDelete": "Are you sure you want to delete this image?",
  "admin.gallery.cropPhotoTitle": "Crop Photo",
  "admin.gallery.cropPhotoTitleWithMore": "Crop Photo (+{{count}} more)",
  "admin.gallery.cropReplacementPhotoTitle": "Crop Replacement Photo",
  "admin.gallery.deleteImage": "Delete Image",
  "admin.gallery.noImages": "No images yet",
  "admin.gallery.pendingUpload": "Pending upload",
  "admin.gallery.replaceImage": "Replace Image",
  "admin.gallery.title": "Gallery",
  "admin.gallery.uploadPhotos": "Upload Photos",
  "admin.gallery.uploading": "Uploading...",

  // ── Admin: Project Type Modal ──────────────────────────────────────────────
  "admin.projectTypeModal.createTitle": "Create Project Type",
  "admin.projectTypeModal.editTitle": "Edit Project Type",
  "admin.projectTypeModal.eyebrow": "Project Type",
  "admin.projectTypeModal.nameLabel": "Name",
  "admin.projectTypeModal.namePlaceholder": "e.g. Fashion Shoot",

  // ── Admin: Project Types Management ────────────────────────────────────────
  "admin.projectTypesManagement.createProjectType": "Create Project Type",
  "admin.projectTypesManagement.created": "Created",
  "admin.projectTypesManagement.deleteConfirmTitle": "Delete this project type?",
  "admin.projectTypesManagement.eyebrow": "Project Types",
  "admin.projectTypesManagement.noProjectTypesFound": "No project types found",
  "admin.projectTypesManagement.noProjectTypesHint": "Create your first project type to get started",
  "admin.projectTypesManagement.searchPlaceholder": "Search project types...",
  "admin.projectTypesManagement.searchResults": "{{count}} results",
  "admin.projectTypesManagement.slug": "Slug",
  "admin.projectTypesManagement.subtitle": "Manage casting brief project types",
  "admin.projectTypesManagement.title": "Project Types",
  "admin.projectTypesManagement.totalProjectTypes": "Total Project Types",

  // ── Admin: Question Modal ──────────────────────────────────────────────────
  "admin.questionModal.activeHint": "Active questions are shown to users",
  "admin.questionModal.addTitle": "Add Question",
  "admin.questionModal.answerType": "Answer Type",
  "admin.questionModal.closeModal": "Close",
  "admin.questionModal.configureSubtitle": "Configure the question details below",
  "admin.questionModal.creating": "Creating...",
  "admin.questionModal.editTitle": "Edit Question",
  "admin.questionModal.questionText": "Question Text",
  "admin.questionModal.questionTextPlaceholder": "Enter the question...",
  "admin.questionModal.questionTextRequired": "Question text is required",
  "admin.questionModal.updating": "Updating...",
  "admin.questionModal.yesNo": "Yes / No",

  // ── Admin: Questions Management ────────────────────────────────────────────
  "admin.questionsManagement.addQuestion": "Add Question",
  "admin.questionsManagement.aria.activateQuestion": "Activate question",
  "admin.questionsManagement.aria.deactivateQuestion": "Deactivate question",
  "admin.questionsManagement.aria.deleteQuestion": "Delete question",
  "admin.questionsManagement.aria.editQuestion": "Edit question",
  "admin.questionsManagement.aria.nextPage": "Next page",
  "admin.questionsManagement.aria.previousPage": "Previous page",
  "admin.questionsManagement.empty.subtitle": "Add your first question to get started",
  "admin.questionsManagement.empty.title": "No questions found",
  "admin.questionsManagement.pagination.showing": "Showing",
  "admin.questionsManagement.registration": "Registration",
  "admin.questionsManagement.searchPlaceholder": "Search questions...",
  "admin.questionsManagement.stats.total": "Total Questions",
  "admin.questionsManagement.subtitle": "Manage registration questions",
  "admin.questionsManagement.table.created": "Created",
  "admin.questionsManagement.table.question": "Question",
  "admin.questionsManagement.title": "Questions Management",

  // ── Admin: Quotations Management ───────────────────────────────────────────
  "admin.quotationsManagement.empty.subtitle": "Quotation requests will appear here",
  "admin.quotationsManagement.empty.title": "No quotations found",
  "admin.quotationsManagement.filter.allStatus": "All Statuses",
  "admin.quotationsManagement.filter.approved": "Approved",
  "admin.quotationsManagement.filter.pending": "Pending",
  "admin.quotationsManagement.filter.rejected": "Rejected",
  "admin.quotationsManagement.stats.pending": "Pending",
  "admin.quotationsManagement.stats.approved": "Approved",
  "admin.quotationsManagement.stats.rejected": "Rejected",
  "admin.quotationsManagement.searchPlaceholder": "Search quotations...",
  "admin.quotationsManagement.subtitle": "Manage client quotation requests",
  "admin.quotationsManagement.table.createdAt": "Created At",
  "admin.quotationsManagement.table.email": "Email",
  "admin.quotationsManagement.table.equipment": "Equipment",
  "admin.quotationsManagement.table.idea": "Idea",
  "admin.quotationsManagement.table.name": "Name",
  "admin.quotationsManagement.table.phone": "Phone",
  "admin.quotationsManagement.title": "Quotations Management",
  "admin.quotationsManagement.total": "Total",

  // ── Admin: Contact Messages ─────────────────────────────────────────────────
  "admin.contactMessages.title": "Contact Messages",
  "admin.contactMessages.subtitle": "Messages submitted from the contact page",
  "admin.contactMessages.total": "Total",
  "admin.contactMessages.stats.new": "New",
  "admin.contactMessages.stats.read": "Read",
  "admin.contactMessages.searchPlaceholder": "Search messages...",
  "admin.contactMessages.filter.allStatus": "All Statuses",
  "admin.contactMessages.empty.title": "No messages found",
  "admin.contactMessages.empty.subtitle": "Messages from the contact form will appear here",
  "admin.contactMessages.table.name": "Name",
  "admin.contactMessages.table.email": "Email",
  "admin.contactMessages.table.phone": "Phone",
  "admin.contactMessages.table.message": "Message",
  "admin.contactMessages.table.createdAt": "Received",

  // ── Admin: Specialties Management ──────────────────────────────────────────
  "admin.specialtiesManagement.createSpecialty": "Create Specialty",
  "admin.specialtiesManagement.created": "Created",
  "admin.specialtiesManagement.deleteConfirmTitle": "Delete this specialty?",
  "admin.specialtiesManagement.eyebrow": "Specialties",
  "admin.specialtiesManagement.noSpecialtiesFound": "No specialties found",
  "admin.specialtiesManagement.noSpecialtiesHint": "Create your first specialty to get started",
  "admin.specialtiesManagement.searchPlaceholder": "Search specialties...",
  "admin.specialtiesManagement.searchResults": "{{count}} results",
  "admin.specialtiesManagement.subOptions": "Sub-options",
  "admin.specialtiesManagement.subtitle": "Manage talent specialties",
  "admin.specialtiesManagement.title": "Specialties",
  "admin.specialtiesManagement.totalSpecialties": "Total Specialties",

  // ── Admin: Specialty Modal ─────────────────────────────────────────────────
  "admin.specialtyModal.createTitle": "Create Specialty",
  "admin.specialtyModal.editTitle": "Edit Specialty",
  "admin.specialtyModal.eyebrow": "Specialty",
  "admin.specialtyModal.nameLabel": "Name",
  "admin.specialtyModal.namePlaceholder": "e.g. Dancer",
  "admin.specialtyModal.optionPlaceholder": "e.g. Ballet",
  "admin.specialtyModal.removeOption": "Remove Option",
  "admin.specialtyModal.subOptionsHint": "Optional sub-categories for this specialty",
  "admin.specialtyModal.subOptionsLabel": "Sub-options",

  // ── Admin: Trusted Brand Modal ─────────────────────────────────────────────
  "admin.trustedBrandModal.addTitle": "Add Trusted Brand",
  "admin.trustedBrandModal.brandNameLabel": "Brand Name",
  "admin.trustedBrandModal.brandNamePlaceholder": "e.g. Nike",
  "admin.trustedBrandModal.editTitle": "Edit Trusted Brand",
  "admin.trustedBrandModal.eyebrow": "Trusted Brand",
  "admin.trustedBrandModal.logoUrlLabel": "Logo URL",
  "admin.trustedBrandModal.logoUrlPlaceholder": "https://...",
  "admin.trustedBrandModal.preview": "Preview",

  // ── Admin: Trusted Brands Management ───────────────────────────────────────
  "admin.trustedBrandsManagement.addBrand": "Add Brand",
  "admin.trustedBrandsManagement.deleteConfirmTitle": "Delete this brand?",
  "admin.trustedBrandsManagement.eyebrow": "Trusted Brands",
  "admin.trustedBrandsManagement.noBrandsFound": "No brands found",
  "admin.trustedBrandsManagement.noBrandsHint": "Add your first trusted brand to get started",
  "admin.trustedBrandsManagement.noImage": "No Image",
  "admin.trustedBrandsManagement.searchPlaceholder": "Search brands...",
  "admin.trustedBrandsManagement.searchResults": "{{count}} results",
  "admin.trustedBrandsManagement.subtitle": "Manage trusted brand logos",
  "admin.trustedBrandsManagement.title": "Trusted Brands",
  "admin.trustedBrandsManagement.totalBrands": "Total Brands",

  // ── Admin: User Modal (validation) ─────────────────────────────────────────
  "admin.userModal.companyRequired": "Company is required",
  "admin.userModal.dobRequired": "Date of birth is required",
  "admin.userModal.emailRequired": "Email is required",
  "admin.userModal.firstNameRequired": "First name is required",
  "admin.userModal.fixHighlightedFields": "Please fix the highlighted fields",
  "admin.userModal.invalidEmail": "Please enter a valid email address",
  "admin.userModal.invalidPhone": "Please enter a valid phone number",
  "admin.userModal.invalidWhatsapp": "Please enter a valid WhatsApp number",
  "admin.userModal.lastNameRequired": "Last name is required",
  "admin.userModal.passwordRequired": "Password is required",
  "admin.userModal.passwordsDontMatch": "Passwords don't match",
  "admin.userModal.selectContentCreatorTypeError": "Please select at least one content creator type",
  "admin.userModal.selectTalentTypeError": "Please select a talent type",

  // ── Admin: Users Management ────────────────────────────────────────────────
  "admin.usersManagement.addNewUser": "Add New User",
  "admin.usersManagement.allRoles": "All Roles",
  "admin.usersManagement.clickToApprove": "Click to approve",
  "admin.usersManagement.clickToSetPending": "Click to set pending",
  "admin.usersManagement.columnEmail": "Email",
  "admin.usersManagement.columnJoined": "Joined",
  "admin.usersManagement.columnPhone": "Phone",
  "admin.usersManagement.columnRole": "Role",
  "admin.usersManagement.columnUser": "User",
  "admin.usersManagement.deleteUserTitle": "Delete this user?",
  "admin.usersManagement.editFullProfile": "Edit Full Profile",
  "admin.usersManagement.next": "Next",
  "admin.usersManagement.pageOf": "Page {{page}} of {{totalPages}}",
  "admin.usersManagement.prev": "Previous",
  "admin.usersManagement.roleAdmin": "Admin",
  "admin.usersManagement.roleClient": "Client",
  "admin.usersManagement.roleTalent": "Talent",
  "admin.usersManagement.searchPlaceholder": "Search users...",
  "admin.usersManagement.showingResults": "Showing {{from}} to {{to}} of {{total}} results",
  "admin.usersManagement.statAdmins": "Admins",
  "admin.usersManagement.statClients": "Clients",
  "admin.usersManagement.statTalents": "Talents",
  "admin.usersManagement.statTotal": "Total Users",
  "admin.usersManagement.subtitle": "Manage all platform users",
  "admin.usersManagement.title": "Users Management",

  // ── Admin: Work Modal ──────────────────────────────────────────────────────
  "admin.workModal.addAward": "Add Award",
  "admin.workModal.awardPlaceholder": "e.g. Best Cinematography",
  "admin.workModal.awards": "Awards",
  "admin.workModal.browse": "Browse",
  "admin.workModal.category": "Category",
  "admin.workModal.categoryPlaceholder": "e.g. Commercial, Music Video",
  "admin.workModal.change": "Change",
  "admin.workModal.client": "Client",
  "admin.workModal.clientPlaceholder": "Client name",
  "admin.workModal.closeModal": "Close",
  "admin.workModal.createWork": "Create Work",
  "admin.workModal.currentImage": "Current Image",
  "admin.workModal.currentVideo": "Current Video",
  "admin.workModal.descriptionPlaceholder": "Write a short description...",
  "admin.workModal.editWork": "Edit Work",
  "admin.workModal.fillDetails": "Fill in the details below",
  "admin.workModal.image": "Image",
  "admin.workModal.imageTooLarge": "Image is too large (max {{maxMb}}MB)",
  "admin.workModal.newWork": "New Work",
  "admin.workModal.noFileSelected": "No file selected",
  "admin.workModal.processing": "Processing...",
  "admin.workModal.removeAward": "Remove Award",
  "admin.workModal.removeFile": "Remove File",
  "admin.workModal.replace": "Replace",
  "admin.workModal.saveChanges": "Save Changes",
  "admin.workModal.titleLabel": "Title",
  "admin.workModal.titlePlaceholder": "Enter a title",
  "admin.workModal.updateDetails": "Update Details",
  "admin.workModal.uploading": "Uploading...",
  "admin.workModal.video": "Video",
  "admin.workModal.videoTooLarge": "Video file is too large",
  "admin.workModal.year": "Year",

  // ── Admin: Works Management ────────────────────────────────────────────────
  "admin.worksManagement.addFirstWork": "Add your first work",
  "admin.worksManagement.addNewWork": "Add New Work",
  "admin.worksManagement.addWork": "Add Work",
  "admin.worksManagement.added": "Added",
  "admin.worksManagement.awards": "Awards",
  "admin.worksManagement.categories": "Categories",
  "admin.worksManagement.clear": "Clear",
  "admin.worksManagement.client": "Client",
  "admin.worksManagement.deleteWork": "Delete Work",
  "admin.worksManagement.editWork": "Edit Work",
  "admin.worksManagement.image": "Image",
  "admin.worksManagement.noMediaAttached": "No media attached",
  "admin.worksManagement.noWorksFound": "No works found",
  "admin.worksManagement.portfolioHub": "Portfolio Hub",
  "admin.worksManagement.searchPlaceholder": "Search works...",
  "admin.worksManagement.subtitle": "Manage your portfolio of work",
  "admin.worksManagement.title": "Works Management",
  "admin.worksManagement.totalWorks": "Total Works",
  "admin.worksManagement.video": "Video",

  // ── Common (extra) ─────────────────────────────────────────────────────────
  "common.unknownError": "An unknown error occurred",

  // ── SweetAlert dialogs ─────────────────────────────────────────────────────
  "swal.blog.approvedText": "The blog post has been approved.",
  "swal.blog.approvedTitle": "Approved!",
  "swal.blog.deleteText": "This action cannot be undone.",
  "swal.blog.deleteTitle": "Delete this blog post?",
  "swal.blog.errorTitle": "Error",
  "swal.blog.goToBlogsButton": "Go to Blogs",
  "swal.blog.loadFailedText": "Failed to load blog posts. Please try again.",
  "swal.blog.rejectInputLabel": "Reason for rejection",
  "swal.blog.rejectInputPlaceholder": "Enter a reason...",
  "swal.blog.rejectTitle": "Reject this blog post?",
  "swal.blog.rejectedTitle": "Rejected",
  "swal.blog.saveFailedText": "Failed to save the blog post. Please try again.",
  "swal.blog.successTitle": "Success!",
  "swal.blog.validationErrorText": "Please fill in all required fields.",
  "swal.blog.validationErrorTitle": "Validation Error",
  "swal.content.createdTitle": "Content created successfully!",
  "swal.content.updatedTitle": "Content updated successfully!",
  "swal.content.validationErrorText": "Please fill in all required fields.",
  "swal.content.validationErrorTitle": "Validation Error",
  "swal.question.deleteConfirmButton": "Yes, delete it",
  "swal.question.deleteText": "This action cannot be undone.",
  "swal.question.deleteTitle": "Delete this question?",

  // ── Toasts ─────────────────────────────────────────────────────────────────
  "toast.album.deleteFailed": "Failed to delete album",
  "toast.album.deleted": "Album deleted",
  "toast.album.imageRequired": "Please select an image",
  "toast.album.imagesAdded": "Images added",
  "toast.album.loadFailed": "Failed to load albums",
  "toast.album.saveFailed": "Failed to save album",
  "toast.album.saved": "Album saved",
  "toast.blog.approveFailed": "Failed to approve blog post",
  "toast.blog.created": "Blog post created",
  "toast.blog.deleteFailed": "Failed to delete blog post",
  "toast.blog.loadFailed": "Failed to load blog posts",
  "toast.blog.rejectFailed": "Failed to reject blog post",
  "toast.blog.updated": "Blog post updated",
  "toast.brief.loadFailed": "Failed to load casting briefs",
  "toast.brief.statusUpdated": "Status updated to {{status}}",
  "toast.brief.updateFailed": "Failed to update status",
  "toast.content.deleteFailed": "Failed to delete content",
  "toast.content.deleted": "Content deleted",
  "toast.content.loadFailed": "Failed to load content",
  "toast.content.saveFailed": "Failed to save content",
  "toast.content.saved": "Content saved",
  "toast.content.titleCategoryRequired": "Title and category are required",
  "toast.gallery.deleteFailed": "Failed to delete image",
  "toast.gallery.exceedsSize": "File exceeds the maximum size",
  "toast.gallery.imageDeleted": "Image deleted",
  "toast.gallery.imageReplaced": "Image replaced",
  "toast.gallery.imagesUploaded": "Images uploaded",
  "toast.gallery.replaceFailed": "Failed to replace image",
  "toast.gallery.unsupportedFormat": "Unsupported file format",
  "toast.gallery.uploadFailed": "Failed to upload image",
  "toast.projectType.created": "Project type created",
  "toast.projectType.deleteFailed": "Failed to delete project type",
  "toast.projectType.deleted": "Project type deleted",
  "toast.projectType.loadFailed": "Failed to load project types",
  "toast.projectType.nameRequired": "Name is required",
  "toast.projectType.saveFailed": "Failed to save project type",
  "toast.projectType.updated": "Project type updated",
  "toast.question.created": "Question created",
  "toast.question.deleteFailed": "Failed to delete question",
  "toast.question.deleted": "Question deleted",
  "toast.question.loadFailed": "Failed to load questions",
  "toast.question.saveFailed": "Failed to save question",
  "toast.question.textRequired": "Question text is required",
  "toast.question.toggleFailed": "Failed to update question status",
  "toast.question.updated": "Question updated",
  "toast.quotation.deleteFailed": "Failed to delete quotation",
  "toast.quotation.deleted": "Quotation deleted",
  "toast.quotation.loadFailed": "Failed to load quotations",
  "toast.quotation.statusUpdateFailed": "Failed to update status",
  "toast.quotation.statusUpdated": "Status updated",
  "toast.quotation.updateFailed": "Failed to update quotation",
  "toast.quotation.updated": "Quotation updated",
  "toast.contactMessage.loadFailed": "Failed to load contact messages",
  "toast.contactMessage.updateFailed": "Failed to update message",
  "toast.contactMessage.deleted": "Message deleted",
  "toast.contactMessage.deleteFailed": "Failed to delete message",
  "toast.specialty.created": "Specialty created",
  "toast.specialty.deleteFailed": "Failed to delete specialty",
  "toast.specialty.deleted": "Specialty deleted",
  "toast.specialty.loadFailed": "Failed to load specialties",
  "toast.specialty.nameRequired": "Name is required",
  "toast.specialty.saveFailed": "Failed to save specialty",
  "toast.specialty.updated": "Specialty updated",
  "toast.testimonial.loadFailed": "Failed to load testimonials",
  "toast.trustedBrand.added": "Brand added",
  "toast.trustedBrand.deleteFailed": "Failed to delete brand",
  "toast.trustedBrand.deleted": "Brand deleted",
  "toast.trustedBrand.loadFailed": "Failed to load brands",
  "toast.trustedBrand.logoInvalidUrl": "Please enter a valid logo URL",
  "toast.trustedBrand.logoRequired": "Logo is required",
  "toast.trustedBrand.nameRequired": "Name is required",
  "toast.trustedBrand.saveFailed": "Failed to save brand",
  "toast.trustedBrand.updated": "Brand updated",
  "toast.user.approved": "User approved",
  "toast.user.createFailed": "Failed to create user",
  "toast.user.created": "User created",
  "toast.user.deleteFailed": "Failed to delete user",
  "toast.user.deleted": "User deleted",
  "toast.user.imageTooLarge": "Image is too large",
  "toast.user.imageUploadFailed": "Failed to upload image",
  "toast.user.invalidEmail": "Please enter a valid email address",
  "toast.user.invalidPhone": "Please enter a valid phone number",
  "toast.user.invalidWhatsapp": "Please enter a valid WhatsApp number",
  "toast.user.loadFailed": "Failed to load user",
  "toast.user.loadListFailed": "Failed to load users",
  "toast.user.profileImageUpdated": "Profile image updated",
  "toast.user.saveFailed": "Failed to save user",
  "toast.user.setPending": "User set to pending",
  "toast.user.statusUpdateFailed": "Failed to update status",
  "toast.user.unsupportedImageFormat": "Unsupported image format",
  "toast.user.updated": "User updated",
  "toast.work.deleteFailed": "Failed to delete work",
  "toast.work.deleted": "Work deleted",
  "toast.work.loadFailed": "Failed to load works",
  "toast.work.saveFailed": "Failed to save work",
  "toast.work.saved": "Work saved",
  "toast.work.titleRequired": "Title is required",

  // ── Shared: Language / Dialect Select ─────────────────────────────────────
  "shared.languageDialectSelect.languages": "Languages *",
  "shared.languageDialectSelect.dialectAccent": "Dialect / Accent",
  "shared.languageDialectSelect.otherLanguageLabel": "Other language — please specify",
  "shared.languageDialectSelect.otherLanguagePlaceholder": "Enter your language",

  // ── Shared: Phone Field ────────────────────────────────────────────────────
  "shared.phoneField.defaultLabel": "Phone",
  "shared.phoneField.alreadyRegistered": "This phone number is already registered.",
  "shared.phoneField.checking": "Checking phone number…",
  "shared.phoneField.invalid": "Enter a valid phone number for the selected country.",

  // ── Shared: Location Select (Country / State / City) ──────────────────────
  "shared.countryStateSelect.country": "Country",
  "shared.countryStateSelect.selectCountry": "Select Country",
  "shared.countryStateSelect.state": "State / Province / Governorate",
  "shared.countryStateSelect.selectState": "Select State / Province / Governorate",
  "shared.countryStateSelect.selectCountryFirst": "Select a country first",
  "shared.countryStateSelect.noStatesAvailable": "No states available",
};
